import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ChildDevelopment.css';
import BackButton from '../../components/BackButton/BackButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faSyringe,
  faMoon,
  faTooth,
  faCamera,
  faRunning,
  faStar,
  faCheck,
  faPlus,
  faTrash,
  faClock,
  faCalendarAlt,
  faChevronDown,
  faChevronUp,
  faChild,
  faTheaterMasks,
  faBalanceScale,
  faBrain,
  faSearch,
  faHeart,
  faComments,
  faBullseye,
  faHandPaper,
  faVolumeUp,
  faDumbbell,
  faUsers,
  faSpa,
  faCircle,
  faCircleCheck,
  faCircleInfo,
  faBed,
  faShieldAlt,
  faChartLine,
  faRulerVertical,
  faWeight,
  faPencilAlt,
  faTimes,
  faUtensils,
  faThermometerHalf,
  faPills,
  faBaby,
} from '@fortawesome/free-solid-svg-icons';
import { Container } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import childDevelopmentService from '../../services/childDevelopmentService';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line,
  ResponsiveContainer, Area, AreaChart, BarChart, Bar,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MemoryEntry {
  id: string;
  title: string;
  date: string;
  description: string;
  photo?: string | null;
}

interface SleepEntry {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface TeethRecord {
  [toothId: number]: { appeared: boolean; date: string; comment: string };
}

interface MilestoneRecord {
  [milestoneId: number]: { achieved: boolean; date: string };
}

interface VaccinationRecord {
  [vaccineId: number]: { completed: boolean; date: string };
}

interface ActivityRecord {
  [key: string]: boolean;
}

interface GrowthEntry {
  id: string;
  date: string;
  heightCm: number | '';
  weightKg: number | '';
  headCircCm: number | '';
  notes: string;
}

interface GrowthMilestone {
  id: number;
  label: string;
  description: string;
  ageRange: string;
  category: 'height' | 'weight' | 'head';
}

interface GrowthMilestoneRecord {
  [milestoneId: number]: { completed: boolean; date: string; value: string };
}

// ─── New Types for Enhanced Features ──────────────────────────────────────────
interface MilestoneAssessment {
  [milestoneId: number]: { status: 'yes' | 'no' | 'almost' | null; date: string };
}

interface MomentRecord {
  [momentId: string]: { completed: boolean; date: string };
}

interface FeedingEntry {
  id: string;
  date: string;
  type: 'breast' | 'bottle' | 'solid';
  startTime: string;
  endTime?: string;
  side?: 'Left' | 'Right' | 'Both';
  amountMl?: number | '';
  bottleType?: 'Formula' | 'Expressed Milk';
  foodDescription?: string;
  notes: string;
}

interface HealthTempEntry {
  id: string;
  date: string;
  time: string;
  temperature: number | '';
  unit: 'F' | 'C';
  notes: string;
}

interface HealthMedEntry {
  id: string;
  date: string;
  time: string;
  medicineName: string;
  dosage: string;
  notes: string;
}

interface DiaperEntry {
  id: string;
  date: string;
  time: string;
  type: 'wet' | 'poop' | 'both' | 'dry';
  color: string;
  consistency: string;
  notes: string;
}

// ─── Milestone Data by Month (Enhanced) ──────────────────────────────────────
interface MilestoneItem {
  id: number;
  text: string;
}

interface MilestoneCategoryData {
  category: string;
  color: string;
  items: MilestoneItem[];
}

const milestonesByMonth: { [monthRange: string]: MilestoneCategoryData[] } = {
  '1': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 101, text: 'Lifts head briefly when on tummy' },
      { id: 102, text: 'Moves arms and legs' },
      { id: 103, text: 'Strong reflex movements' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 111, text: 'Focuses on faces 8-12 inches away' },
      { id: 112, text: 'Recognizes some sounds' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 121, text: 'Cries to express needs' },
      { id: 122, text: 'Makes throaty sounds' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 131, text: 'Calms down when spoken to or picked up' },
      { id: 132, text: 'Looks at parent\'s face' },
    ]},
  ],
  '2': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 201, text: 'Holds head up when on tummy' },
      { id: 202, text: 'Makes smoother arm movements' },
      { id: 203, text: 'Opens and closes hands' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 211, text: 'Pays attention to faces' },
      { id: 212, text: 'Begins to follow things with eyes' },
      { id: 213, text: 'Recognizes people at a distance' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 221, text: 'Coos and makes gurgling sounds' },
      { id: 222, text: 'Turns head toward sounds' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 231, text: 'First social smile' },
      { id: 232, text: 'Briefly calms self (sucks on hand)' },
      { id: 233, text: 'Tries to look at parent' },
    ]},
  ],
  '3': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 301, text: 'Raises head and chest when on tummy' },
      { id: 302, text: 'Opens and closes hands' },
      { id: 303, text: 'Pushes down on legs when on flat surface' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 311, text: 'Follows moving objects with eyes' },
      { id: 312, text: 'Watches faces intently' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 321, text: 'Babbles and coos' },
      { id: 322, text: 'Smiles at the sound of your voice' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 331, text: 'Begins to develop a social smile' },
      { id: 332, text: 'Enjoys playing with others' },
    ]},
  ],
  '4': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 401, text: 'Holds head steady without support' },
      { id: 402, text: 'Pushes up to elbows on tummy' },
      { id: 403, text: 'May be able to roll from tummy to back' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 411, text: 'Reaches for toy with one hand' },
      { id: 412, text: 'Follows moving things with eyes' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 421, text: 'Begins to babble' },
      { id: 422, text: 'Copies some movements and facial expressions' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 431, text: 'Smiles spontaneously at people' },
      { id: 432, text: 'Likes to play with people' },
    ]},
  ],
  '6': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 601, text: 'Rolls over in both directions' },
      { id: 602, text: 'Begins to sit without support' },
      { id: 603, text: 'Rocks back and forth' },
      { id: 604, text: 'Supports weight on legs and might bounce' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 611, text: 'Looks around at things nearby' },
      { id: 612, text: 'Brings things to mouth' },
      { id: 613, text: 'Shows curiosity and tries to get out of reach things' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 621, text: 'Responds to sounds by making sounds' },
      { id: 622, text: 'Strings vowels together ("ah", "eh", "oh")' },
      { id: 623, text: 'Responds to own name' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 631, text: 'Knows familiar faces' },
      { id: 632, text: 'Likes to play with others, especially parents' },
      { id: 633, text: 'Responds to other people\'s emotions' },
    ]},
  ],
  '9': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 901, text: 'Stands holding on' },
      { id: 902, text: 'Can get into sitting position' },
      { id: 903, text: 'Sits without support' },
      { id: 904, text: 'Crawls' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 911, text: 'Watches the path of something as it falls' },
      { id: 912, text: 'Looks for things hidden' },
      { id: 913, text: 'Plays peek-a-boo' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 921, text: 'Understands "no"' },
      { id: 922, text: 'Makes a lot of different sounds' },
      { id: 923, text: 'Copies sounds and gestures' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 931, text: 'May be afraid of strangers' },
      { id: 932, text: 'May be clingy with familiar adults' },
      { id: 933, text: 'Has favorite toys' },
    ]},
  ],
  '12': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 1201, text: 'Gets to sitting position without help' },
      { id: 1202, text: 'Pulls up to stand, walks holding on' },
      { id: 1203, text: 'May take a few steps without holding on' },
      { id: 1204, text: 'May stand alone' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 1211, text: 'Explores things in different ways (shaking, banging, throwing)' },
      { id: 1212, text: 'Finds hidden things easily' },
      { id: 1213, text: 'Looks at the right picture when named' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 1221, text: 'Responds to simple spoken requests' },
      { id: 1222, text: 'Uses simple gestures like shaking head "no"' },
      { id: 1223, text: 'Says "mama" and "dada" and exclamations' },
      { id: 1224, text: 'Tries to say words you say' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 1231, text: 'Is shy or nervous with strangers' },
      { id: 1232, text: 'Cries when mom or dad leaves' },
      { id: 1233, text: 'Has favorite things and people' },
      { id: 1234, text: 'Hands you a book to read' },
    ]},
  ],
  '15': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 1501, text: 'Walks alone' },
      { id: 1502, text: 'May walk up steps and run' },
      { id: 1503, text: 'Pulls toys while walking' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 1511, text: 'Knows what ordinary things are for (telephone, spoon)' },
      { id: 1512, text: 'Points to get attention' },
      { id: 1513, text: 'Stacks at least two small objects' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 1521, text: 'Says several single words' },
      { id: 1522, text: 'Says and shakes head "no"' },
      { id: 1523, text: 'Points to show someone what they want' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 1531, text: 'Copies other children' },
      { id: 1532, text: 'Shows you an object they like' },
      { id: 1533, text: 'Claps when excited' },
    ]},
  ],
  '18': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 1801, text: 'Walks alone' },
      { id: 1802, text: 'May walk up steps and run' },
      { id: 1803, text: 'Pulls toys while walking' },
      { id: 1804, text: 'Helps undress self' },
      { id: 1805, text: 'Drinks from a cup' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 1811, text: 'Knows what ordinary things are for' },
      { id: 1812, text: 'Points to get attention of others' },
      { id: 1813, text: 'Shows interest in a doll or stuffed animal' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 1821, text: 'Says several single words' },
      { id: 1822, text: 'Says and shakes head "no"' },
      { id: 1823, text: 'Points to show someone what they want' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 1831, text: 'May have temper tantrums' },
      { id: 1832, text: 'May be afraid of strangers' },
      { id: 1833, text: 'Shows affection to familiar people' },
      { id: 1834, text: 'Plays simple pretend' },
    ]},
  ],
  '24': [
    { category: 'Physical', color: '#4DB6AC', items: [
      { id: 2401, text: 'Stands on tiptoe' },
      { id: 2402, text: 'Kicks a ball' },
      { id: 2403, text: 'Begins to run' },
      { id: 2404, text: 'Climbs on and off furniture without help' },
      { id: 2405, text: 'Walks up and down stairs holding on' },
    ]},
    { category: 'Cognitive', color: '#9575CD', items: [
      { id: 2411, text: 'Finds things hidden under 2-3 covers' },
      { id: 2412, text: 'Begins to sort shapes and colors' },
      { id: 2413, text: 'Completes sentences in familiar books' },
      { id: 2414, text: 'Plays simple make-believe games' },
    ]},
    { category: 'Communication', color: '#FFB74D', items: [
      { id: 2421, text: 'Points to things in a book' },
      { id: 2422, text: 'Says sentences with 2 to 4 words' },
      { id: 2423, text: 'Follows simple instructions' },
      { id: 2424, text: 'Repeats words overheard' },
    ]},
    { category: 'Social & Emotional', color: '#F48FB1', items: [
      { id: 2431, text: 'Copies others, especially adults' },
      { id: 2432, text: 'Gets excited when with other children' },
      { id: 2433, text: 'Shows more independence' },
      { id: 2434, text: 'Shows defiant behavior' },
    ]},
  ],
};

const milestoneMonthKeys = Object.keys(milestonesByMonth);

// ─── Feeding Guide Reference Data ────────────────────────────────────────────
const feedingGuideData = [
  { ageRange: '0-1 mo', minMonth: 0, maxMonth: 1, source: 'Breast/Formula', amountPerFeed: '60-90 ml', feedsPerDay: '8-12', solids: 'None' },
  { ageRange: '1-2 mo', minMonth: 1, maxMonth: 2, source: 'Breast/Formula', amountPerFeed: '90-120 ml', feedsPerDay: '7-9', solids: 'None' },
  { ageRange: '2-4 mo', minMonth: 2, maxMonth: 4, source: 'Breast/Formula', amountPerFeed: '120-150 ml', feedsPerDay: '6-8', solids: 'None' },
  { ageRange: '4-6 mo', minMonth: 4, maxMonth: 6, source: 'Breast/Formula', amountPerFeed: '120-180 ml', feedsPerDay: '5-7', solids: 'Intro purees' },
  { ageRange: '6-9 mo', minMonth: 6, maxMonth: 9, source: 'Breast/Formula', amountPerFeed: '180-240 ml', feedsPerDay: '4-6', solids: '2-3 meals' },
  { ageRange: '9-12 mo', minMonth: 9, maxMonth: 12, source: 'Breast/Formula', amountPerFeed: '180-240 ml', feedsPerDay: '3-5', solids: '3 meals + snacks' },
  { ageRange: '12-18 mo', minMonth: 12, maxMonth: 18, source: 'Whole milk', amountPerFeed: '240-360 ml/day', feedsPerDay: '2-3', solids: '3 meals + 2 snacks' },
  { ageRange: '18-24 mo', minMonth: 18, maxMonth: 24, source: 'Whole milk', amountPerFeed: '360-480 ml/day', feedsPerDay: '2-3', solids: '3 meals + 2 snacks' },
];

// ─── Sleep Guide Reference Data ──────────────────────────────────────────────
const sleepGuideData = [
  { ageRange: '0-1 mo', minMonth: 0, maxMonth: 1, totalSleep: '15-17 hrs', nightSleep: '8-9 hrs', naps: '3-5', napDuration: 'Variable' },
  { ageRange: '1-3 mo', minMonth: 1, maxMonth: 3, totalSleep: '14-17 hrs', nightSleep: '8-10 hrs', naps: '3-4', napDuration: '30min-2hrs' },
  { ageRange: '3-6 mo', minMonth: 3, maxMonth: 6, totalSleep: '12-16 hrs', nightSleep: '9-11 hrs', naps: '2-3', napDuration: '1-2 hrs' },
  { ageRange: '6-9 mo', minMonth: 6, maxMonth: 9, totalSleep: '12-15 hrs', nightSleep: '10-12 hrs', naps: '2', napDuration: '1-2 hrs' },
  { ageRange: '9-12 mo', minMonth: 9, maxMonth: 12, totalSleep: '12-15 hrs', nightSleep: '10-12 hrs', naps: '1-2', napDuration: '1-2 hrs' },
  { ageRange: '12-18 mo', minMonth: 12, maxMonth: 18, totalSleep: '11-14 hrs', nightSleep: '10-12 hrs', naps: '1-2', napDuration: '1-2 hrs' },
  { ageRange: '18-24 mo', minMonth: 18, maxMonth: 24, totalSleep: '11-14 hrs', nightSleep: '10-12 hrs', naps: '1', napDuration: '1-2 hrs' },
];

// Moments presets for Memories tab
const momentPresets = [
  { id: 'first_meal', label: 'First Meal' },
  { id: 'rolling_over', label: 'Rolling Over' },
  { id: 'sitting_up', label: 'Sitting Up' },
  { id: 'first_crawl', label: 'First Crawl' },
  { id: 'clapping', label: 'Clapping' },
  { id: 'found_feet', label: 'Found My Feet' },
  { id: 'standing', label: 'Standing' },
  { id: 'peek_a_boo', label: 'Peek-a-boo' },
  { id: 'first_step', label: 'First Step' },
  { id: 'first_word', label: 'First Word' },
  { id: 'says_mama', label: 'Says Mama' },
  { id: 'says_dada', label: 'Says Dada' },
  { id: 'first_tooth', label: '1st Tooth' },
  { id: 'first_haircut', label: 'First Hair Cut' },
  { id: 'waved_bye', label: 'Waved Bye-Bye' },
  { id: 'drank_cup', label: 'Drank From Cup' },
  { id: 'first_swim', label: 'First Swim' },
  { id: 'sleep_through', label: 'Sleep Through Night' },
  { id: 'first_shoes', label: 'First Shoes' },
  { id: 'first_jump', label: 'First Jump' },
];

// ─── Data ─────────────────────────────────────────────────────────────────────
const activityCategories = [
  { group: 'Acting & Expression', icon: faTheaterMasks, color: '#ff5722', activities: ['Tongue Movements', 'Facial Expressions', 'Gestures'] },
  { group: 'Balancing', icon: faBalanceScale, color: '#9c27b0', activities: ['Tilt Balancing', 'Sitting Posture', 'Sitting Practice'] },
  { group: 'Concept Learning', icon: faBrain, color: '#3f51b5', activities: ['Definition of No', 'Object Permanence', 'Cause and Effect'] },
  { group: 'Early Exploration', icon: faSearch, color: '#009688', activities: ['Early Talks', 'Mirror Playing', 'Exploring Textures'] },
  { group: 'Expressing Emotions', icon: faHeart, color: '#e91e63', activities: ['Signs of Affection', 'Pet Friend', 'Comfort Seeking'] },
  { group: 'Language Comprehension', icon: faComments, color: '#ff9800', activities: ['Recognizing Names', 'Explain Action to Baby', 'Says Goodbye'] },
  { group: 'Memory & Attention', icon: faBullseye, color: '#795548', activities: ['Object Permanence', 'Combine Actions', 'Focused Play'] },
  { group: 'Sensory Development', icon: faHandPaper, color: '#607d8b', activities: ['Temperature Sense', 'Texture Exploration', 'Sound Recognition'] },
  { group: 'Speech Development', icon: faVolumeUp, color: '#2196f3', activities: ['Noise and Sound', 'Talk and Move', 'Imitation Game', 'Lip Sounds'] },
  { group: 'Motor Skills', icon: faDumbbell, color: '#4caf50', activities: ['Creeping', 'Squats', 'Walking on Legs', 'Roll Over', 'Neck Movement', 'Grasping Small Objects'] },
  { group: 'Social Skills', icon: faUsers, color: '#ff5722', activities: ['Explore Surroundings', 'Rhyme and Dance', 'Toys Play'] },
  { group: 'Emotional Balance', icon: faSpa, color: '#673ab7', activities: ['Sounds of Nature', 'Calm Music', 'Comfort Routine'] },
];

const milestones = [
  { id: 1, name: 'First Smile', ageRange: '6-8 weeks', category: 'Social' },
  { id: 2, name: 'First Laugh', ageRange: '3-4 months', category: 'Social' },
  { id: 3, name: 'Holds Head Up', ageRange: '2-4 months', category: 'Motor' },
  { id: 4, name: 'Rolls Over', ageRange: '4-6 months', category: 'Motor' },
  { id: 5, name: 'Sits Without Support', ageRange: '6-8 months', category: 'Motor' },
  { id: 6, name: 'First Crawl', ageRange: '7-10 months', category: 'Motor' },
  { id: 7, name: 'First Words', ageRange: '10-14 months', category: 'Language' },
  { id: 8, name: 'First Steps', ageRange: '9-15 months', category: 'Motor' },
  { id: 9, name: 'Waves Goodbye', ageRange: '9-12 months', category: 'Social' },
  { id: 10, name: 'Picks Up Small Objects', ageRange: '9-12 months', category: 'Motor' },
  { id: 11, name: 'Says Mama/Dada', ageRange: '8-12 months', category: 'Language' },
  { id: 12, name: 'Stands Alone', ageRange: '10-14 months', category: 'Motor' },
  { id: 13, name: 'Drinks From Cup', ageRange: '12-15 months', category: 'Self-Care' },
  { id: 14, name: 'Follows Simple Instructions', ageRange: '12-18 months', category: 'Language' },
  { id: 15, name: 'Runs', ageRange: '15-18 months', category: 'Motor' },
  { id: 16, name: 'Speaks 2-Word Phrases', ageRange: '18-24 months', category: 'Language' },
];

// All 20 primary (baby) teeth with dental arch positions
// Upper jaw: right to left (patient perspective) = 2nd Molar, 1st Molar, Canine, Lateral Incisor, Central Incisor x2, Lateral Incisor, Canine, 1st Molar, 2nd Molar
// Lower jaw: mirrors upper
const teethData = [
  // Upper jaw - right side
  { id: 1, name: 'Upper Right 2nd Molar', position: 'top', jaw: 'upper' as const, toothType: 'Second Molar', typicalAge: '25-33 months', angle: -72 },
  { id: 2, name: 'Upper Right 1st Molar', position: 'top', jaw: 'upper' as const, toothType: 'First Molar', typicalAge: '13-19 months', angle: -54 },
  { id: 3, name: 'Upper Right Canine', position: 'top', jaw: 'upper' as const, toothType: 'Canine', typicalAge: '16-22 months', angle: -36 },
  { id: 4, name: 'Upper Right Lateral Incisor', position: 'top', jaw: 'upper' as const, toothType: 'Lateral Incisor', typicalAge: '9-13 months', angle: -18 },
  { id: 5, name: 'Upper Right Central Incisor', position: 'top', jaw: 'upper' as const, toothType: 'Central Incisor', typicalAge: '8-12 months', angle: -6 },
  // Upper jaw - left side
  { id: 6, name: 'Upper Left Central Incisor', position: 'top', jaw: 'upper' as const, toothType: 'Central Incisor', typicalAge: '8-12 months', angle: 6 },
  { id: 7, name: 'Upper Left Lateral Incisor', position: 'top', jaw: 'upper' as const, toothType: 'Lateral Incisor', typicalAge: '9-13 months', angle: 18 },
  { id: 8, name: 'Upper Left Canine', position: 'top', jaw: 'upper' as const, toothType: 'Canine', typicalAge: '16-22 months', angle: 36 },
  { id: 9, name: 'Upper Left 1st Molar', position: 'top', jaw: 'upper' as const, toothType: 'First Molar', typicalAge: '13-19 months', angle: 54 },
  { id: 10, name: 'Upper Left 2nd Molar', position: 'top', jaw: 'upper' as const, toothType: 'Second Molar', typicalAge: '25-33 months', angle: 72 },
  // Lower jaw - right side
  { id: 11, name: 'Lower Right 2nd Molar', position: 'bottom', jaw: 'lower' as const, toothType: 'Second Molar', typicalAge: '23-31 months', angle: -72 },
  { id: 12, name: 'Lower Right 1st Molar', position: 'bottom', jaw: 'lower' as const, toothType: 'First Molar', typicalAge: '14-18 months', angle: -54 },
  { id: 13, name: 'Lower Right Canine', position: 'bottom', jaw: 'lower' as const, toothType: 'Canine', typicalAge: '17-23 months', angle: -36 },
  { id: 14, name: 'Lower Right Lateral Incisor', position: 'bottom', jaw: 'lower' as const, toothType: 'Lateral Incisor', typicalAge: '10-16 months', angle: -18 },
  { id: 15, name: 'Lower Right Central Incisor', position: 'bottom', jaw: 'lower' as const, toothType: 'Central Incisor', typicalAge: '6-10 months', angle: -6 },
  // Lower jaw - left side
  { id: 16, name: 'Lower Left Central Incisor', position: 'bottom', jaw: 'lower' as const, toothType: 'Central Incisor', typicalAge: '6-10 months', angle: 6 },
  { id: 17, name: 'Lower Left Lateral Incisor', position: 'bottom', jaw: 'lower' as const, toothType: 'Lateral Incisor', typicalAge: '10-16 months', angle: 18 },
  { id: 18, name: 'Lower Left Canine', position: 'bottom', jaw: 'lower' as const, toothType: 'Canine', typicalAge: '17-23 months', angle: 36 },
  { id: 19, name: 'Lower Left 1st Molar', position: 'bottom', jaw: 'lower' as const, toothType: 'First Molar', typicalAge: '14-18 months', angle: 54 },
  { id: 20, name: 'Lower Left 2nd Molar', position: 'bottom', jaw: 'lower' as const, toothType: 'Second Molar', typicalAge: '23-31 months', angle: 72 },
];

const vaccinationSchedule = [
  { id: 1, name: 'BCG', age: 'At Birth', description: 'Bacillus Calmette-Guerin vaccine for tuberculosis prevention' },
  { id: 2, name: 'OPV-0', age: 'At Birth', description: 'Oral Polio Vaccine - Zero dose' },
  { id: 3, name: 'Hepatitis B - Birth Dose', age: 'At Birth', description: 'First dose of Hepatitis B vaccine' },
  { id: 4, name: 'OPV-1 + IPV-1', age: '6 Weeks', description: 'Oral Polio Vaccine + Injectable Polio Vaccine' },
  { id: 5, name: 'DPT-1', age: '6 Weeks', description: 'Diphtheria, Pertussis, Tetanus - First dose' },
  { id: 6, name: 'Hepatitis B - 2nd Dose', age: '6 Weeks', description: 'Second dose of Hepatitis B vaccine' },
  { id: 7, name: 'Hib-1', age: '6 Weeks', description: 'Haemophilus Influenzae type B - First dose' },
  { id: 8, name: 'Rotavirus-1', age: '6 Weeks', description: 'Rotavirus vaccine - First dose' },
  { id: 9, name: 'PCV-1', age: '6 Weeks', description: 'Pneumococcal Conjugate Vaccine - First dose' },
  { id: 10, name: 'OPV-2 + IPV-2', age: '10 Weeks', description: 'Oral Polio + Injectable Polio - Second dose' },
  { id: 11, name: 'DPT-2', age: '10 Weeks', description: 'Diphtheria, Pertussis, Tetanus - Second dose' },
  { id: 12, name: 'Hib-2', age: '10 Weeks', description: 'Haemophilus Influenzae type B - Second dose' },
  { id: 13, name: 'Rotavirus-2', age: '10 Weeks', description: 'Rotavirus vaccine - Second dose' },
  { id: 14, name: 'OPV-3 + IPV-3', age: '14 Weeks', description: 'Oral Polio + Injectable Polio - Third dose' },
  { id: 15, name: 'DPT-3', age: '14 Weeks', description: 'Diphtheria, Pertussis, Tetanus - Third dose' },
  { id: 16, name: 'Hib-3', age: '14 Weeks', description: 'Haemophilus Influenzae type B - Third dose' },
  { id: 17, name: 'Rotavirus-3', age: '14 Weeks', description: 'Rotavirus vaccine - Third dose' },
  { id: 18, name: 'PCV-2', age: '14 Weeks', description: 'Pneumococcal Conjugate Vaccine - Second dose' },
  { id: 19, name: 'Influenza-1', age: '6 Months', description: 'Influenza vaccine - First dose' },
  { id: 20, name: 'Hepatitis B - 3rd Dose', age: '6 Months', description: 'Third dose of Hepatitis B vaccine' },
  { id: 21, name: 'OPV-4', age: '9 Months', description: 'Oral Polio Vaccine - Fourth dose' },
  { id: 22, name: 'Measles/MMR-1', age: '9 Months', description: 'Measles or MMR vaccine - First dose' },
  { id: 23, name: 'PCV Booster', age: '15 Months', description: 'Pneumococcal Conjugate Vaccine Booster' },
  { id: 24, name: 'MMR-2', age: '15 Months', description: 'MMR vaccine - Second dose' },
  { id: 25, name: 'Varicella-1', age: '15 Months', description: 'Chickenpox vaccine - First dose' },
  { id: 26, name: 'DPT Booster-1', age: '18 Months', description: 'DPT Booster - First' },
  { id: 27, name: 'Hib Booster', age: '18 Months', description: 'Haemophilus Influenzae type B Booster' },
  { id: 28, name: 'Hepatitis A - 1st Dose', age: '18 Months', description: 'Hepatitis A vaccine - First dose' },
  { id: 29, name: 'Hepatitis A - 2nd Dose', age: '24 Months', description: 'Hepatitis A vaccine - Second dose' },
  { id: 30, name: 'Typhoid', age: '24 Months', description: 'Typhoid vaccine' },
  { id: 31, name: 'DPT Booster-2', age: '5 Years', description: 'DPT Booster - Second' },
  { id: 32, name: 'OPV-5', age: '5 Years', description: 'Oral Polio Vaccine - Fifth dose' },
  { id: 33, name: 'Varicella-2', age: '5 Years', description: 'Chickenpox vaccine - Second dose' },
];

const growthMilestones: GrowthMilestone[] = [
  { id: 1, label: 'Birth Weight Doubled', description: 'Baby has doubled their birth weight', ageRange: '4-6 months', category: 'weight' },
  { id: 2, label: 'Birth Weight Tripled', description: 'Baby has tripled their birth weight', ageRange: '12 months', category: 'weight' },
  { id: 3, label: 'Reaches 50 cm', description: 'Height reaches 50 cm mark', ageRange: '0-2 months', category: 'height' },
  { id: 4, label: 'Reaches 60 cm', description: 'Height reaches 60 cm mark', ageRange: '2-4 months', category: 'height' },
  { id: 5, label: 'Reaches 70 cm', description: 'Height reaches 70 cm mark', ageRange: '6-9 months', category: 'height' },
  { id: 6, label: 'Reaches 75 cm', description: 'Height reaches 75 cm mark', ageRange: '9-12 months', category: 'height' },
  { id: 7, label: 'Reaches 80 cm', description: 'Height reaches 80 cm', ageRange: '12-18 months', category: 'height' },
  { id: 8, label: 'Reaches 85 cm', description: 'Height reaches 85 cm', ageRange: '18-24 months', category: 'height' },
  { id: 9, label: 'Head Circ. 35 cm', description: 'Head circumference reaches 35 cm', ageRange: '0-1 month', category: 'head' },
  { id: 10, label: 'Head Circ. 40 cm', description: 'Head circumference reaches 40 cm', ageRange: '3-4 months', category: 'head' },
  { id: 11, label: 'Head Circ. 45 cm', description: 'Head circumference reaches 45 cm', ageRange: '9-12 months', category: 'head' },
  { id: 12, label: 'Head Circ. 47 cm', description: 'Head circumference reaches 47 cm', ageRange: '18-24 months', category: 'head' },
  { id: 13, label: 'Weight 5 kg', description: 'Baby reaches 5 kg', ageRange: '2-3 months', category: 'weight' },
  { id: 14, label: 'Weight 8 kg', description: 'Baby reaches 8 kg', ageRange: '6-8 months', category: 'weight' },
  { id: 15, label: 'Weight 10 kg', description: 'Baby reaches 10 kg', ageRange: '10-14 months', category: 'weight' },
];

// ─── WHO Child Growth Standards (0-24 months) ────────────────────────────────
// Source: WHO Multicentre Growth Reference Study (Length/Height-for-age,
// Weight-for-age, Head circumference-for-age). Percentiles: 3rd, 50th, 97th.
const whoReferenceData = {
  boys: {
    height: [
      { month: 0, p3: 46.3, p50: 49.9, p97: 53.4 },
      { month: 1, p3: 51.1, p50: 54.7, p97: 58.4 },
      { month: 2, p3: 54.7, p50: 58.4, p97: 62.2 },
      { month: 3, p3: 57.6, p50: 61.4, p97: 65.3 },
      { month: 4, p3: 59.9, p50: 63.9, p97: 67.8 },
      { month: 5, p3: 61.8, p50: 65.9, p97: 70.0 },
      { month: 6, p3: 63.4, p50: 67.6, p97: 71.9 },
      { month: 7, p3: 64.8, p50: 69.2, p97: 73.5 },
      { month: 8, p3: 66.2, p50: 70.6, p97: 75.0 },
      { month: 9, p3: 67.5, p50: 72.0, p97: 76.5 },
      { month: 10, p3: 68.7, p50: 73.3, p97: 77.9 },
      { month: 11, p3: 69.9, p50: 74.5, p97: 79.2 },
      { month: 12, p3: 71.0, p50: 75.7, p97: 80.5 },
      { month: 13, p3: 72.1, p50: 76.9, p97: 81.8 },
      { month: 14, p3: 73.1, p50: 78.0, p97: 83.0 },
      { month: 15, p3: 74.1, p50: 79.1, p97: 84.2 },
      { month: 16, p3: 75.0, p50: 80.2, p97: 85.4 },
      { month: 17, p3: 76.0, p50: 81.2, p97: 86.5 },
      { month: 18, p3: 76.9, p50: 82.3, p97: 87.7 },
      { month: 19, p3: 77.7, p50: 83.2, p97: 88.8 },
      { month: 20, p3: 78.6, p50: 84.2, p97: 89.8 },
      { month: 21, p3: 79.4, p50: 85.1, p97: 90.9 },
      { month: 22, p3: 80.2, p50: 86.0, p97: 91.9 },
      { month: 23, p3: 81.0, p50: 86.9, p97: 92.9 },
      { month: 24, p3: 81.7, p50: 87.8, p97: 93.9 },
    ],
    weight: [
      { month: 0, p3: 2.5, p50: 3.3, p97: 4.4 },
      { month: 1, p3: 3.4, p50: 4.5, p97: 5.8 },
      { month: 2, p3: 4.3, p50: 5.6, p97: 7.1 },
      { month: 3, p3: 5.0, p50: 6.4, p97: 8.0 },
      { month: 4, p3: 5.6, p50: 7.0, p97: 8.7 },
      { month: 5, p3: 6.0, p50: 7.5, p97: 9.3 },
      { month: 6, p3: 6.4, p50: 7.9, p97: 9.8 },
      { month: 7, p3: 6.7, p50: 8.3, p97: 10.3 },
      { month: 8, p3: 6.9, p50: 8.6, p97: 10.7 },
      { month: 9, p3: 7.1, p50: 8.9, p97: 11.0 },
      { month: 10, p3: 7.4, p50: 9.2, p97: 11.4 },
      { month: 11, p3: 7.6, p50: 9.4, p97: 11.7 },
      { month: 12, p3: 7.7, p50: 9.6, p97: 12.0 },
      { month: 13, p3: 7.9, p50: 9.9, p97: 12.3 },
      { month: 14, p3: 8.1, p50: 10.1, p97: 12.6 },
      { month: 15, p3: 8.3, p50: 10.3, p97: 12.8 },
      { month: 16, p3: 8.4, p50: 10.5, p97: 13.1 },
      { month: 17, p3: 8.6, p50: 10.7, p97: 13.4 },
      { month: 18, p3: 8.8, p50: 10.9, p97: 13.7 },
      { month: 19, p3: 8.9, p50: 11.1, p97: 13.9 },
      { month: 20, p3: 9.1, p50: 11.3, p97: 14.2 },
      { month: 21, p3: 9.2, p50: 11.5, p97: 14.5 },
      { month: 22, p3: 9.4, p50: 11.8, p97: 14.7 },
      { month: 23, p3: 9.5, p50: 12.0, p97: 15.0 },
      { month: 24, p3: 9.7, p50: 12.2, p97: 15.3 },
    ],
    head: [
      { month: 0, p3: 32.4, p50: 34.5, p97: 36.7 },
      { month: 1, p3: 35.2, p50: 37.3, p97: 39.5 },
      { month: 2, p3: 36.9, p50: 39.1, p97: 41.3 },
      { month: 3, p3: 38.3, p50: 40.5, p97: 42.7 },
      { month: 4, p3: 39.4, p50: 41.6, p97: 43.8 },
      { month: 5, p3: 40.3, p50: 42.6, p97: 44.8 },
      { month: 6, p3: 41.0, p50: 43.3, p97: 45.6 },
      { month: 7, p3: 41.7, p50: 44.0, p97: 46.3 },
      { month: 8, p3: 42.2, p50: 44.5, p97: 46.9 },
      { month: 9, p3: 42.6, p50: 45.0, p97: 47.4 },
      { month: 10, p3: 43.0, p50: 45.4, p97: 47.8 },
      { month: 11, p3: 43.4, p50: 45.8, p97: 48.2 },
      { month: 12, p3: 43.6, p50: 46.1, p97: 48.5 },
      { month: 13, p3: 43.9, p50: 46.3, p97: 48.8 },
      { month: 14, p3: 44.1, p50: 46.6, p97: 49.0 },
      { month: 15, p3: 44.3, p50: 46.8, p97: 49.3 },
      { month: 16, p3: 44.5, p50: 47.0, p97: 49.5 },
      { month: 17, p3: 44.7, p50: 47.2, p97: 49.7 },
      { month: 18, p3: 44.9, p50: 47.4, p97: 49.9 },
      { month: 19, p3: 45.0, p50: 47.5, p97: 50.1 },
      { month: 20, p3: 45.2, p50: 47.7, p97: 50.2 },
      { month: 21, p3: 45.3, p50: 47.8, p97: 50.4 },
      { month: 22, p3: 45.4, p50: 48.0, p97: 50.5 },
      { month: 23, p3: 45.6, p50: 48.1, p97: 50.7 },
      { month: 24, p3: 45.7, p50: 48.3, p97: 50.8 },
    ],
  },
  girls: {
    height: [
      { month: 0, p3: 45.6, p50: 49.1, p97: 52.7 },
      { month: 1, p3: 50.0, p50: 53.7, p97: 57.4 },
      { month: 2, p3: 53.2, p50: 57.1, p97: 60.9 },
      { month: 3, p3: 55.8, p50: 59.8, p97: 63.8 },
      { month: 4, p3: 57.8, p50: 62.1, p97: 66.2 },
      { month: 5, p3: 59.6, p50: 64.0, p97: 68.2 },
      { month: 6, p3: 61.0, p50: 65.7, p97: 70.0 },
      { month: 7, p3: 62.4, p50: 67.3, p97: 71.6 },
      { month: 8, p3: 63.7, p50: 68.7, p97: 73.2 },
      { month: 9, p3: 64.9, p50: 70.1, p97: 74.7 },
      { month: 10, p3: 66.1, p50: 71.5, p97: 76.1 },
      { month: 11, p3: 67.2, p50: 72.8, p97: 77.5 },
      { month: 12, p3: 68.3, p50: 74.0, p97: 78.9 },
      { month: 13, p3: 69.3, p50: 75.2, p97: 80.2 },
      { month: 14, p3: 70.3, p50: 76.4, p97: 81.5 },
      { month: 15, p3: 71.2, p50: 77.5, p97: 82.7 },
      { month: 16, p3: 72.1, p50: 78.6, p97: 83.9 },
      { month: 17, p3: 73.0, p50: 79.7, p97: 85.0 },
      { month: 18, p3: 73.9, p50: 80.7, p97: 86.1 },
      { month: 19, p3: 74.7, p50: 81.7, p97: 87.2 },
      { month: 20, p3: 75.6, p50: 82.7, p97: 88.3 },
      { month: 21, p3: 76.4, p50: 83.7, p97: 89.4 },
      { month: 22, p3: 77.2, p50: 84.6, p97: 90.4 },
      { month: 23, p3: 78.0, p50: 85.5, p97: 91.4 },
      { month: 24, p3: 78.7, p50: 86.4, p97: 92.5 },
    ],
    weight: [
      { month: 0, p3: 2.4, p50: 3.2, p97: 4.2 },
      { month: 1, p3: 3.2, p50: 4.2, p97: 5.5 },
      { month: 2, p3: 3.9, p50: 5.1, p97: 6.6 },
      { month: 3, p3: 4.5, p50: 5.8, p97: 7.5 },
      { month: 4, p3: 5.0, p50: 6.4, p97: 8.2 },
      { month: 5, p3: 5.4, p50: 6.9, p97: 8.8 },
      { month: 6, p3: 5.7, p50: 7.3, p97: 9.3 },
      { month: 7, p3: 6.0, p50: 7.6, p97: 9.8 },
      { month: 8, p3: 6.3, p50: 7.9, p97: 10.2 },
      { month: 9, p3: 6.5, p50: 8.2, p97: 10.5 },
      { month: 10, p3: 6.7, p50: 8.5, p97: 10.9 },
      { month: 11, p3: 6.9, p50: 8.7, p97: 11.2 },
      { month: 12, p3: 7.0, p50: 8.9, p97: 11.5 },
      { month: 13, p3: 7.2, p50: 9.2, p97: 11.8 },
      { month: 14, p3: 7.4, p50: 9.4, p97: 12.1 },
      { month: 15, p3: 7.6, p50: 9.6, p97: 12.4 },
      { month: 16, p3: 7.7, p50: 9.8, p97: 12.6 },
      { month: 17, p3: 7.9, p50: 10.0, p97: 12.9 },
      { month: 18, p3: 8.1, p50: 10.2, p97: 13.2 },
      { month: 19, p3: 8.2, p50: 10.4, p97: 13.5 },
      { month: 20, p3: 8.4, p50: 10.6, p97: 13.7 },
      { month: 21, p3: 8.6, p50: 10.9, p97: 14.0 },
      { month: 22, p3: 8.7, p50: 11.1, p97: 14.3 },
      { month: 23, p3: 8.9, p50: 11.3, p97: 14.6 },
      { month: 24, p3: 9.0, p50: 11.5, p97: 14.8 },
    ],
    head: [
      { month: 0, p3: 31.7, p50: 33.9, p97: 36.1 },
      { month: 1, p3: 34.3, p50: 36.5, p97: 38.8 },
      { month: 2, p3: 35.8, p50: 38.3, p97: 40.7 },
      { month: 3, p3: 37.1, p50: 39.5, p97: 42.0 },
      { month: 4, p3: 38.1, p50: 40.6, p97: 43.1 },
      { month: 5, p3: 38.9, p50: 41.5, p97: 44.0 },
      { month: 6, p3: 39.6, p50: 42.2, p97: 44.8 },
      { month: 7, p3: 40.2, p50: 42.8, p97: 45.5 },
      { month: 8, p3: 40.7, p50: 43.4, p97: 46.0 },
      { month: 9, p3: 41.2, p50: 43.8, p97: 46.5 },
      { month: 10, p3: 41.5, p50: 44.2, p97: 46.9 },
      { month: 11, p3: 41.9, p50: 44.6, p97: 47.3 },
      { month: 12, p3: 42.2, p50: 44.9, p97: 47.6 },
      { month: 13, p3: 42.4, p50: 45.2, p97: 47.9 },
      { month: 14, p3: 42.7, p50: 45.4, p97: 48.2 },
      { month: 15, p3: 42.9, p50: 45.7, p97: 48.4 },
      { month: 16, p3: 43.1, p50: 45.9, p97: 48.6 },
      { month: 17, p3: 43.3, p50: 46.1, p97: 48.8 },
      { month: 18, p3: 43.5, p50: 46.2, p97: 49.0 },
      { month: 19, p3: 43.6, p50: 46.4, p97: 49.2 },
      { month: 20, p3: 43.8, p50: 46.6, p97: 49.4 },
      { month: 21, p3: 43.9, p50: 46.7, p97: 49.5 },
      { month: 22, p3: 44.1, p50: 46.9, p97: 49.7 },
      { month: 23, p3: 44.2, p50: 47.0, p97: 49.8 },
      { month: 24, p3: 44.3, p50: 47.2, p97: 50.0 },
    ],
  },
};

type TabType = 'Activities' | 'Growth' | 'Milestones' | 'Memories' | 'Teething' | 'Vaccination' | 'Sleeping' | 'Feeding' | 'Health';

const tabConfig: { key: TabType; label: string; icon: any; color: string }[] = [
  { key: 'Activities', label: 'Activities', icon: faRunning, color: '#0891B2' },
  { key: 'Growth', label: 'Growth', icon: faChartLine, color: '#6366f1' },
  { key: 'Milestones', label: 'Milestones', icon: faStar, color: '#f59e0b' },
  { key: 'Memories', label: 'Memories', icon: faCamera, color: '#ec4899' },
  { key: 'Feeding', label: 'Feeding', icon: faUtensils, color: '#f97316' },
  { key: 'Health', label: 'Health', icon: faThermometerHalf, color: '#ef4444' },
  { key: 'Teething', label: 'Teething', icon: faTooth, color: '#0891B2' },
  { key: 'Vaccination', label: 'Vaccination', icon: faShieldAlt, color: '#059669' },
  { key: 'Sleeping', label: 'Sleeping', icon: faMoon, color: '#8b5cf6' },
];

const pastelColors = ['#f0f9ff', '#fdf2f8', '#f5f3ff', '#ecfdf5', '#fffbeb', '#fef2f2'];

// ─── Helper: Vaccination age-to-days mapping for overdue logic ────────────────
const ageToApproxDays = (ageStr: string): number | null => {
  const lower = ageStr.toLowerCase().trim();
  if (lower === 'at birth') return 0;
  const weeksMatch = lower.match(/^(\d+)\s*weeks?$/);
  if (weeksMatch) return parseInt(weeksMatch[1]) * 7;
  const monthsMatch = lower.match(/^(\d+)\s*months?$/);
  if (monthsMatch) return parseInt(monthsMatch[1]) * 30;
  const yearsMatch = lower.match(/^(\d+)\s*years?$/);
  if (yearsMatch) return parseInt(yearsMatch[1]) * 365;
  return null;
};

// ─── Helper: Calculate sleep duration ─────────────────────────────────────────
const calcSleepDuration = (startTime: string, endTime: string): string => {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let diffMin = (eh * 60 + em) - (sh * 60 + sm);
  if (diffMin < 0) diffMin += 24 * 60;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return `${hours}h ${mins}m`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const ChildDevelopment: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['childDev', 'common']);

  // ── Tab state ──
  const [activeTab, setActiveTab] = useState<TabType>('Activities');

  // ── Child profile state ──
  const [childDob, setChildDob] = useState('');
  const [childName, setChildName] = useState('');
  const [showChildProfileEdit, setShowChildProfileEdit] = useState(false);

  // ── Activities state ──
  const [activityRecords, setActivityRecords] = useState<ActivityRecord>({});
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  // ── Milestones state ──
  const [milestoneRecords, setMilestoneRecords] = useState<MilestoneRecord>({});

  // ── Memories state ──
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [showMemoryForm, setShowMemoryForm] = useState(false);
  const [memoryForm, setMemoryForm] = useState({ title: '', date: '', description: '' });
  const [memoryPhoto, setMemoryPhoto] = useState<File | null>(null);

  // ── Teething state ──
  const [teethRecords, setTeethRecords] = useState<TeethRecord>({});

  // ── Vaccination state ──
  const [vaccinationRecords, setVaccinationRecords] = useState<VaccinationRecord>({});

  // ── Sleeping state ──
  const [sleepEntries, setSleepEntries] = useState<SleepEntry[]>([]);
  const [showSleepForm, setShowSleepForm] = useState(false);
  const [sleepForm, setSleepForm] = useState({ date: '', startTime: '', endTime: '' });
  const [sleepCalendarMonth, setSleepCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // ── Sleep Timer state ──
  const [sleepTimerRunning, setSleepTimerRunning] = useState(false);
  const [sleepTimerStart, setSleepTimerStart] = useState<number | null>(null);
  const [sleepTimerElapsed, setSleepTimerElapsed] = useState(0);

  // ── Growth state ──
  const [growthEntries, setGrowthEntries] = useState<GrowthEntry[]>([]);
  const [showGrowthForm, setShowGrowthForm] = useState(false);
  const [growthForm, setGrowthForm] = useState<Omit<GrowthEntry, 'id'>>({ date: '', heightCm: '', weightKg: '', headCircCm: '', notes: '' });
  const [editingGrowthId, setEditingGrowthId] = useState<string | null>(null);
  const growthFormRef = React.useRef<HTMLDivElement>(null);
  const [growthMilestoneRecords, setGrowthMilestoneRecords] = useState<GrowthMilestoneRecord>({});
  const [activeGrowthChart, setActiveGrowthChart] = useState<'height' | 'weight' | 'head'>('height');
  const [childGender, setChildGender] = useState<'boy' | 'girl' | 'neutral'>('neutral');
  const [growthTablePage, setGrowthTablePage] = useState(0);
  const growthTablePageSize = 5;

  // ── Enhanced Milestones state ──
  const [milestoneAssessments, setMilestoneAssessments] = useState<MilestoneAssessment>({});
  const [selectedMilestoneMonth, setSelectedMilestoneMonth] = useState<string>('1');
  const [milestoneMonthAutoSelected, setMilestoneMonthAutoSelected] = useState(false);
  const [expandedMilestoneCategory, setExpandedMilestoneCategory] = useState<string | null>('Physical');
  const [showMilestoneComparison, setShowMilestoneComparison] = useState(false);

  // ── Moments state (Memories) ──
  const [moments, setMoments] = useState<MomentRecord>({});

  // ── Feeding state ──
  const [feedingEntries, setFeedingEntries] = useState<FeedingEntry[]>([]);
  const [feedingSubTab, setFeedingSubTab] = useState<'breast' | 'bottle' | 'solid' | 'diaper' | 'guide' | 'summary'>('breast');
  const [showFeedingForm, setShowFeedingForm] = useState(false);
  const [feedingDate, setFeedingDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [feedingForm, setFeedingForm] = useState<Omit<FeedingEntry, 'id' | 'date' | 'type'>>({
    startTime: '', endTime: '', side: 'Left', amountMl: '', bottleType: 'Formula', foodDescription: '', notes: '',
  });

  // ── Diaper state ──
  const [diaperEntries, setDiaperEntries] = useState<DiaperEntry[]>([]);
  const [showDiaperForm, setShowDiaperForm] = useState(false);
  const [diaperForm, setDiaperForm] = useState({ time: '', type: 'wet' as DiaperEntry['type'], color: '', consistency: '', notes: '' });

  // ── Sleep guide state ──
  const [showSleepGuide, setShowSleepGuide] = useState(false);

  // ── Health state ──
  const [healthTempEntries, setHealthTempEntries] = useState<HealthTempEntry[]>([]);
  const [healthMedEntries, setHealthMedEntries] = useState<HealthMedEntry[]>([]);
  const [healthSubTab, setHealthSubTab] = useState<'temperature' | 'medication'>('temperature');
  const [showHealthForm, setShowHealthForm] = useState(false);
  const [healthTempForm, setHealthTempForm] = useState<Omit<HealthTempEntry, 'id'>>({ date: '', time: '', temperature: '', unit: 'F', notes: '' });
  const [healthMedForm, setHealthMedForm] = useState<Omit<HealthMedEntry, 'id'>>({ date: '', time: '', medicineName: '', dosage: '', notes: '' });

  // ── Child selection & API state ──
  const [children, setChildren] = useState<any[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [showMigrationPrompt, setShowMigrationPrompt] = useState(false);
  const [showChildSelector, setShowChildSelector] = useState(false);
  const [showAddChildForm, setShowAddChildForm] = useState(false);
  const [showChildrenDialog, setShowChildrenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const askDelete = (title: string, message: string, onConfirm: () => void) => setDeleteConfirm({ title, message, onConfirm });
  const [newChildForm, setNewChildForm] = useState({ name: '', gender: 'neutral', dob: '' });
  const [profileFormError, setProfileFormError] = useState('');

  // ── Load children from API ──
  useEffect(() => {
    const loadChildren = async () => {
      setChildrenLoading(true);
      try {
        const childList = await childDevelopmentService.getChildren();
        setChildren(childList);

        if (childList.length > 0) {
          setSelectedChildId(childList[0].id);
        } else {
          // Check if there's localStorage data to migrate
          const hasLocalData = localStorage.getItem('childDev_name') || localStorage.getItem('childDev_dob');
          if (hasLocalData) {
            setShowMigrationPrompt(true);
          }
        }
      } catch (err) {
        console.error('Failed to load children:', err);
        // Fallback: load from localStorage if API fails (offline support)
        loadFromLocalStorage();
      } finally {
        setChildrenLoading(false);
      }
    };
    loadChildren();
  }, []);

  // Fallback: load from localStorage (used when API is unavailable)
  const loadFromLocalStorage = () => {
    try { const v = localStorage.getItem('childDev_dob'); if (v) setChildDob(v); } catch {}
    try { const v = localStorage.getItem('childDev_name'); if (v) setChildName(v); } catch {}
    try { const v = localStorage.getItem('childDev_activities'); if (v) setActivityRecords(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_milestones'); if (v) setMilestoneRecords(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_memories'); if (v) setMemories(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_teething'); if (v) setTeethRecords(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_vaccinations'); if (v) setVaccinationRecords(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_sleep'); if (v) setSleepEntries(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_growth'); if (v) setGrowthEntries(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_growthMilestones'); if (v) setGrowthMilestoneRecords(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_gender'); if (v === 'boy' || v === 'girl' || v === 'neutral') setChildGender(v); } catch {}
    try { const v = localStorage.getItem('childDev_milestoneAssessments'); if (v) setMilestoneAssessments(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_moments'); if (v) setMoments(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_feeding'); if (v) setFeedingEntries(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_diapers'); if (v) setDiaperEntries(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_healthTemp'); if (v) setHealthTempEntries(JSON.parse(v)); } catch {}
    try { const v = localStorage.getItem('childDev_healthMed'); if (v) setHealthMedEntries(JSON.parse(v)); } catch {}
  };

  // ── Load selected child's full data from API ──
  useEffect(() => {
    if (!selectedChildId) return;
    const loadChildData = async () => {
      setDataLoading(true);
      try {
        const data = await childDevelopmentService.getFullData(selectedChildId);
        if (data.profile) {
          setChildName(data.profile.name || '');
          setChildDob(data.profile.dob || '');
          const g = data.profile.gender;
          if (g === 'boy' || g === 'girl' || g === 'neutral') setChildGender(g);
        }
        if (data.growth) setGrowthEntries(data.growth.map((g: any) => ({
          id: String(g.id), date: g.date, heightCm: g.height_cm ?? '', weightKg: g.weight_kg ?? '', headCircCm: g.head_circ_cm ?? '', notes: g.notes || '',
        })));
        if (data.milestones) {
          const mr: MilestoneRecord = {};
          const ma: MilestoneAssessment = {};
          data.milestones.forEach((m: any) => {
            const key = Number(m.milestone_key) || m.milestone_key;
            mr[key] = { achieved: m.achieved, date: m.achieved_date || '' };
            if (m.status) ma[key] = { status: m.status, date: m.achieved_date || '' };
          });
          setMilestoneRecords(mr);
          setMilestoneAssessments(ma);
        }
        if (data.vaccinations) {
          const vr: VaccinationRecord = {};
          data.vaccinations.forEach((v: any) => { vr[v.vaccine_id] = { completed: v.completed, date: v.completed_date || '' }; });
          setVaccinationRecords(vr);
        }
        if (data.teeth) {
          const tr: TeethRecord = {};
          data.teeth.forEach((t: any) => { tr[t.tooth_id] = { appeared: t.appeared, date: t.appeared_date || '', comment: t.comment || '' }; });
          setTeethRecords(tr);
        }
        if (data.sleep) setSleepEntries(data.sleep.map((s: any) => ({
          id: String(s.id), date: s.date, startTime: s.start_time, endTime: s.end_time,
        })));
        if (data.feeding) setFeedingEntries(data.feeding.map((f: any) => ({
          id: String(f.id), date: f.date, type: f.feeding_type, startTime: f.start_time, endTime: f.end_time,
          side: f.side, amountMl: f.amount_ml ?? '', bottleType: f.bottle_type, foodDescription: f.food_description, notes: f.notes || '',
        })));
        if (data.diapers) setDiaperEntries(data.diapers.map((d: any) => ({
          id: String(d.id), date: d.date, time: d.time, type: d.diaper_type, color: d.color || '', consistency: d.consistency || '', notes: d.notes || '',
        })));
        if (data.health_temp) setHealthTempEntries(data.health_temp.map((h: any) => ({
          id: String(h.id), date: h.date, time: h.time, temperature: h.temperature ?? '', unit: h.unit || 'F', notes: h.notes || '',
        })));
        if (data.health_med) setHealthMedEntries(data.health_med.map((h: any) => ({
          id: String(h.id), date: h.date, time: h.time, medicineName: h.medicine_name, dosage: h.dosage, notes: h.notes || '',
        })));
        if (data.memories) setMemories(data.memories.map((m: any) => ({
          id: String(m.id), title: m.title, date: m.date, description: m.description, photo: m.photo || null,
        })));
        if (data.activities) {
          const ar: ActivityRecord = {};
          data.activities.forEach((a: any) => { ar[a.activity_key] = a.completed; });
          setActivityRecords(ar);
        }
        if (data.moments) {
          const mo: MomentRecord = {};
          data.moments.forEach((m: any) => { mo[m.moment_key] = { completed: m.completed, date: m.completed_date || '' }; });
          setMoments(mo);
        }
        if (data.growth_milestones) {
          const gm: GrowthMilestoneRecord = {};
          data.growth_milestones.forEach((g: any) => { gm[g.milestone_id] = { completed: g.completed, date: g.completed_date || '', value: g.value || '' }; });
          setGrowthMilestoneRecords(gm);
        }
      } catch (err) {
        console.error('Failed to load child data:', err);
      } finally {
        setDataLoading(false);
      }
    };
    loadChildData();
  }, [selectedChildId]);

  // ── Migration handler: localStorage -> API ──
  const handleMigration = async () => {
    setMigrating(true);
    try {
      const name = localStorage.getItem('childDev_name') || 'My Child';
      const dob = localStorage.getItem('childDev_dob') || new Date().toISOString().split('T')[0];
      const gender = localStorage.getItem('childDev_gender') || 'neutral';

      const child = await childDevelopmentService.createChild({ name, gender, dob });

      // Build bulk import payload from localStorage
      const importData: any = {};
      try { const v = localStorage.getItem('childDev_growth'); if (v) importData.growth = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_milestones'); if (v) importData.milestones = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_milestoneAssessments'); if (v) importData.milestone_assessments = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_vaccinations'); if (v) importData.vaccinations = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_teething'); if (v) importData.teeth = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_sleep'); if (v) importData.sleep = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_feeding'); if (v) importData.feeding = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_diapers'); if (v) importData.diapers = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_healthTemp'); if (v) importData.health_temp = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_healthMed'); if (v) importData.health_med = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_memories'); if (v) importData.memories = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_activities'); if (v) importData.activities = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_moments'); if (v) importData.moments = JSON.parse(v); } catch {}
      try { const v = localStorage.getItem('childDev_growthMilestones'); if (v) importData.growth_milestones = JSON.parse(v); } catch {}

      if (Object.keys(importData).length > 0) {
        await childDevelopmentService.bulkImport(child.id, importData);
      }

      // Clear all localStorage keys
      const keys = [
        'childDev_dob', 'childDev_name', 'childDev_gender', 'childDev_activities',
        'childDev_milestones', 'childDev_memories', 'childDev_teething', 'childDev_vaccinations',
        'childDev_sleep', 'childDev_growth', 'childDev_growthMilestones', 'childDev_milestoneAssessments',
        'childDev_moments', 'childDev_feeding', 'childDev_diapers', 'childDev_healthTemp', 'childDev_healthMed',
      ];
      keys.forEach(k => localStorage.removeItem(k));

      // Refresh state
      setSelectedChildId(child.id);
      setChildren([child]);
      setShowMigrationPrompt(false);
    } catch (err) {
      console.error('Migration failed:', err);
    } finally {
      setMigrating(false);
    }
  };

  // ── Add new child via API ──
  const handleAddChild = async () => {
    if (!newChildForm.name || !newChildForm.dob) return;
    try {
      const child = await childDevelopmentService.createChild({
        name: newChildForm.name,
        gender: newChildForm.gender,
        dob: newChildForm.dob,
      });
      setChildren(prev => [...prev, child]);
      setSelectedChildId(child.id);
      setShowAddChildForm(false);
      setNewChildForm({ name: '', gender: 'neutral', dob: '' });
    } catch (err) {
      console.error('Failed to create child:', err);
    }
  };

  // ── Helper: update child profile on API (debounced/fire-and-forget) ──
  const updateChildProfile = (field: string, value: string) => {
    if (!selectedChildId) return;
    childDevelopmentService.updateChild(selectedChildId, { [field]: value }).catch(err => {
      console.error('Failed to update child profile:', err);
    });
  };

  // ── Save helpers (optimistic update + async API persist) ──
  const saveActivities = (updated: ActivityRecord, changedKey?: string) => {
    setActivityRecords(updated);
    if (selectedChildId && changedKey) {
      childDevelopmentService.saveActivity(selectedChildId, {
        activity_key: changedKey, completed: updated[changedKey],
      }).catch(err => console.error('Failed to save activity:', err));
    }
  };

  const saveMilestones = (updated: MilestoneRecord, changedKey?: number) => {
    setMilestoneRecords(updated);
    if (selectedChildId && changedKey !== undefined) {
      const rec = updated[changedKey];
      childDevelopmentService.saveMilestone(selectedChildId, {
        milestone_key: changedKey, achieved: rec?.achieved ?? false, achieved_date: rec?.date || null,
      }).catch(err => console.error('Failed to save milestone:', err));
    }
  };

  const saveMemories = (updated: MemoryEntry[], added?: MemoryEntry, photoFile?: File | null) => {
    setMemories(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveMemory(selectedChildId, {
        title: added.title, date: added.date, description: added.description, photo: photoFile || null,
      }).then(res => {
        // Update the local entry with the server-assigned ID and stored photo URL
        setMemories(prev => prev.map(m => m.id === added.id ? { ...m, id: String(res.id), photo: res.photo || m.photo } : m));
      }).catch(err => console.error('Failed to save memory:', err));
    }
  };

  const deleteMemoryEntry = (entryId: string) => {
    setMemories(prev => prev.filter(m => m.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteMemory(selectedChildId, numId).catch(err => console.error('Failed to delete memory:', err));
      }
    }
  };

  const saveTeeth = (updated: TeethRecord, changedToothId?: number) => {
    setTeethRecords(updated);
    if (selectedChildId && changedToothId !== undefined) {
      const rec = updated[changedToothId];
      childDevelopmentService.saveTooth(selectedChildId, {
        tooth_id: changedToothId, appeared: rec?.appeared ?? false, appeared_date: rec?.date || null, comment: rec?.comment || '',
      }).catch(err => console.error('Failed to save tooth:', err));
    }
  };

  const saveVaccinations = (updated: VaccinationRecord, changedVaccineId?: number) => {
    setVaccinationRecords(updated);
    if (selectedChildId && changedVaccineId !== undefined) {
      const rec = updated[changedVaccineId];
      childDevelopmentService.saveVaccination(selectedChildId, {
        vaccine_id: changedVaccineId, completed: rec?.completed ?? false, completed_date: rec?.date || null,
      }).catch(err => console.error('Failed to save vaccination:', err));
    }
  };

  const saveSleep = (updated: SleepEntry[], added?: SleepEntry) => {
    setSleepEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveSleepEntry(selectedChildId, {
        date: added.date, start_time: added.startTime, end_time: added.endTime,
      }).then(res => {
        setSleepEntries(prev => prev.map(s => s.id === added.id ? { ...s, id: String(res.id) } : s));
      }).catch(err => console.error('Failed to save sleep entry:', err));
    }
  };

  const deleteSleepEntry = (entryId: string) => {
    setSleepEntries(prev => prev.filter(s => s.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteSleepEntry(selectedChildId, numId).catch(err => console.error('Failed to delete sleep entry:', err));
      }
    }
  };

  const saveGrowthEntries = (updated: GrowthEntry[], added?: GrowthEntry) => {
    setGrowthEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveGrowthEntry(selectedChildId, {
        date: added.date, height_cm: added.heightCm, weight_kg: added.weightKg, head_circ_cm: added.headCircCm, notes: added.notes,
      }).then(res => {
        setGrowthEntries(prev => prev.map(g => g.id === added.id ? { ...g, id: String(res.id) } : g));
      }).catch(err => console.error('Failed to save growth entry:', err));
    }
  };

  const growthMilestoneThresholds: Record<number, { field: 'heightCm' | 'weightKg' | 'headCircCm'; min: number }> = {
    3: { field: 'heightCm', min: 50 },
    4: { field: 'heightCm', min: 60 },
    5: { field: 'heightCm', min: 70 },
    6: { field: 'heightCm', min: 75 },
    7: { field: 'heightCm', min: 80 },
    8: { field: 'heightCm', min: 85 },
    9: { field: 'headCircCm', min: 35 },
    10: { field: 'headCircCm', min: 40 },
    11: { field: 'headCircCm', min: 45 },
    12: { field: 'headCircCm', min: 47 },
    13: { field: 'weightKg', min: 5 },
    14: { field: 'weightKg', min: 8 },
    15: { field: 'weightKg', min: 10 },
  };

  const syncGrowthMilestonesFromEntries = (entries: GrowthEntry[]) => {
    setGrowthMilestoneRecords(prev => {
      let changed = false;
      const next: GrowthMilestoneRecord = { ...prev };
      Object.entries(growthMilestoneThresholds).forEach(([idStr, cfg]) => {
        const id = Number(idStr);
        const record = next[id];
        if (!record?.completed) return;
        const stillSupported = entries.some(e => Number(e[cfg.field]) >= cfg.min);
        if (!stillSupported) {
          next[id] = { completed: false, date: '', value: '' };
          changed = true;
          if (selectedChildId) {
            childDevelopmentService.saveGrowthMilestone(selectedChildId, {
              milestone_id: id, completed: false, completed_date: null, value: '',
            }).catch(err => console.error('Failed to un-set growth milestone:', err));
          }
        }
      });
      return changed ? next : prev;
    });
  };

  const deleteGrowthEntry = (entryId: string) => {
    setGrowthEntries(prev => {
      const nextEntries = prev.filter(g => g.id !== entryId);
      syncGrowthMilestonesFromEntries(nextEntries);
      return nextEntries;
    });
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteGrowthEntry(selectedChildId, numId).catch(err => console.error('Failed to delete growth entry:', err));
      }
    }
  };

  const saveGrowthMilestones = (updated: GrowthMilestoneRecord, changedId?: number) => {
    setGrowthMilestoneRecords(updated);
    if (selectedChildId && changedId !== undefined) {
      const rec = updated[changedId];
      childDevelopmentService.saveGrowthMilestone(selectedChildId, {
        milestone_id: changedId, completed: rec?.completed ?? false, completed_date: rec?.date || null, value: rec?.value || '',
      }).catch(err => console.error('Failed to save growth milestone:', err));
    }
  };

  const saveMilestoneAssessments = (updated: MilestoneAssessment, changedKey?: number) => {
    setMilestoneAssessments(updated);
    if (selectedChildId && changedKey !== undefined) {
      const rec = updated[changedKey];
      childDevelopmentService.saveMilestone(selectedChildId, {
        milestone_key: changedKey, status: rec?.status || null, achieved: rec?.status === 'yes', achieved_date: rec?.date || null,
      }).catch(err => console.error('Failed to save milestone assessment:', err));
    }
  };

  const saveMoments = (updated: MomentRecord, changedKey?: string) => {
    setMoments(updated);
    if (selectedChildId && changedKey) {
      const rec = updated[changedKey];
      childDevelopmentService.saveMoment(selectedChildId, {
        moment_key: changedKey, completed: rec?.completed ?? false, completed_date: rec?.date || null,
      }).catch(err => console.error('Failed to save moment:', err));
    }
  };

  const saveFeedingEntries = (updated: FeedingEntry[], added?: FeedingEntry) => {
    setFeedingEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveFeedingEntry(selectedChildId, {
        date: added.date, feeding_type: added.type, start_time: added.startTime, end_time: added.endTime,
        side: added.side, amount_ml: added.amountMl, bottle_type: added.bottleType,
        food_description: added.foodDescription, notes: added.notes,
      }).then(res => {
        setFeedingEntries(prev => prev.map(f => f.id === added.id ? { ...f, id: String(res.id) } : f));
      }).catch(err => console.error('Failed to save feeding entry:', err));
    }
  };

  const deleteFeedingEntry = (entryId: string) => {
    setFeedingEntries(prev => prev.filter(f => f.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteFeedingEntry(selectedChildId, numId).catch(err => console.error('Failed to delete feeding entry:', err));
      }
    }
  };

  const saveDiaperEntries = (updated: DiaperEntry[], added?: DiaperEntry) => {
    setDiaperEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveDiaperEntry(selectedChildId, {
        date: added.date, time: added.time, diaper_type: added.type, color: added.color,
        consistency: added.consistency, notes: added.notes,
      }).then(res => {
        setDiaperEntries(prev => prev.map(d => d.id === added.id ? { ...d, id: String(res.id) } : d));
      }).catch(err => console.error('Failed to save diaper entry:', err));
    }
  };

  const deleteDiaperEntry = (entryId: string) => {
    setDiaperEntries(prev => prev.filter(d => d.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteDiaperEntry(selectedChildId, numId).catch(err => console.error('Failed to delete diaper entry:', err));
      }
    }
  };

  const saveHealthTempEntries = (updated: HealthTempEntry[], added?: HealthTempEntry) => {
    setHealthTempEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveHealthTemp(selectedChildId, {
        date: added.date, time: added.time, temperature: added.temperature, unit: added.unit, notes: added.notes,
      }).then(res => {
        setHealthTempEntries(prev => prev.map(h => h.id === added.id ? { ...h, id: String(res.id) } : h));
      }).catch(err => console.error('Failed to save health temp:', err));
    }
  };

  const deleteHealthTempEntry = (entryId: string) => {
    setHealthTempEntries(prev => prev.filter(h => h.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteHealthTemp(selectedChildId, numId).catch(err => console.error('Failed to delete health temp:', err));
      }
    }
  };

  const saveHealthMedEntries = (updated: HealthMedEntry[], added?: HealthMedEntry) => {
    setHealthMedEntries(updated);
    if (selectedChildId && added) {
      childDevelopmentService.saveHealthMed(selectedChildId, {
        date: added.date, time: added.time, medicine_name: added.medicineName, dosage: added.dosage, notes: added.notes,
      }).then(res => {
        setHealthMedEntries(prev => prev.map(h => h.id === added.id ? { ...h, id: String(res.id) } : h));
      }).catch(err => console.error('Failed to save health med:', err));
    }
  };

  const deleteHealthMedEntry = (entryId: string) => {
    setHealthMedEntries(prev => prev.filter(h => h.id !== entryId));
    if (selectedChildId) {
      const numId = Number(entryId);
      if (!isNaN(numId)) {
        childDevelopmentService.deleteHealthMed(selectedChildId, numId).catch(err => console.error('Failed to delete health med:', err));
      }
    }
  };

  // ── Location carousel handlers ──


  // ── Milestone progress ──
  const milestonesAchieved = milestones.filter((m) => milestoneRecords[m.id]?.achieved).length;
  const milestonePercent = Math.round((milestonesAchieved / milestones.length) * 100);

  // ── Vaccination grouping ──
  const vaccinationGroups: { [age: string]: typeof vaccinationSchedule } = {};
  vaccinationSchedule.forEach((v) => {
    if (!vaccinationGroups[v.age]) vaccinationGroups[v.age] = [];
    vaccinationGroups[v.age].push(v);
  });

  const getVaccinationStatus = (vaccineAge: string): 'completed' | 'due' | 'overdue' => {
    if (!childDob) return 'due';
    const ageDays = ageToApproxDays(vaccineAge);
    if (ageDays === null) return 'due';
    const dob = new Date(childDob);
    const dueDate = new Date(dob.getTime() + ageDays * 24 * 60 * 60 * 1000);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today > dueDate) return 'overdue';
    return 'due';
  };

  // ── Sleep calendar helpers ──
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const sleepDatesSet = new Set(sleepEntries.map((e) => e.date));

  // ── Sleep Timer Logic ──
  useEffect(() => {
    if (!sleepTimerRunning || !sleepTimerStart) return;
    const interval = setInterval(() => {
      setSleepTimerElapsed(Date.now() - sleepTimerStart);
    }, 1000);
    return () => clearInterval(interval);
  }, [sleepTimerRunning, sleepTimerStart]);

  const formatTimerElapsed = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleSleepTimerToggle = () => {
    if (sleepTimerRunning) {
      // Stop timer and save entry
      const startDate = new Date(sleepTimerStart!);
      const endDate = new Date();
      const dateStr = startDate.toISOString().split('T')[0];
      const startTimeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
      const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
      const newEntry: SleepEntry = {
        id: Date.now().toString(),
        date: dateStr,
        startTime: startTimeStr,
        endTime: endTimeStr,
      };
      const updated = [newEntry, ...sleepEntries];
      saveSleep(updated, newEntry);
      setSleepTimerRunning(false);
      setSleepTimerStart(null);
      setSleepTimerElapsed(0);
    } else {
      // Start timer
      setSleepTimerStart(Date.now());
      setSleepTimerElapsed(0);
      setSleepTimerRunning(true);
    }
  };

  const prevMonth = () => {
    setSleepCalendarMonth((prev) => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setSleepCalendarMonth((prev) => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  // Vaccination completed count
  const vaccCompleted = vaccinationSchedule.filter((v) => vaccinationRecords[v.id]?.completed).length;
  const vaccPercent = Math.round((vaccCompleted / vaccinationSchedule.length) * 100);

  // Teething count
  const teethAppeared = teethData.filter((t) => teethRecords[t.id]?.appeared).length;

  // ── Growth chart data merged with WHO reference ──
  const growthChartData = useMemo(() => {
    return [...growthEntries]
      .filter((e) => e.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((e) => ({
        date: new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        rawDate: e.date,
        height: e.heightCm !== '' ? Number(e.heightCm) : null,
        weight: e.weightKg !== '' ? Number(e.weightKg) : null,
        head: e.headCircCm !== '' ? Number(e.headCircCm) : null,
      }));
  }, [growthEntries]);

  // ── WHO helper: interpolate percentile values at any fractional month ──
  const interpolateWho = (whoData: { month: number; p3: number; p50: number; p97: number }[], ageMonths: number) => {
    if (ageMonths <= 0) return whoData[0];
    if (ageMonths >= 24) return whoData[whoData.length - 1];
    const lo = whoData[Math.floor(ageMonths)];
    const hi = whoData[Math.ceil(ageMonths)];
    if (lo.month === hi.month) return lo;
    const t = ageMonths - lo.month;
    return {
      p3: +(lo.p3 + t * (hi.p3 - lo.p3)).toFixed(1),
      p50: +(lo.p50 + t * (hi.p50 - lo.p50)).toFixed(1),
      p97: +(lo.p97 + t * (hi.p97 - lo.p97)).toFixed(1),
    };
  };

  // ── WHO helper: get age in months from entry date ──
  const getAgeMonths = (entryDate: string): number | null => {
    if (!childDob || !entryDate) return null;
    return Math.round((new Date(entryDate).getTime() - new Date(childDob).getTime()) / (30.44 * 24 * 60 * 60 * 1000));
  };

  // ── WHO helper: get ideal (p50) for a metric at an age ──
  const getWhoIdeal = (ageMonths: number, metric: 'height' | 'weight' | 'head') => {
    const ref = childGender === 'girl' ? whoReferenceData.girls : whoReferenceData.boys;
    return interpolateWho(ref[metric], ageMonths).p50;
  };

  // ── WHO helper: estimate approximate percentile via linear interpolation
  // between the p3, p50 and p97 reference values. Rough but illustrative for parents.
  const estimatePercentile = (value: number, who: { p3: number; p50: number; p97: number }): number => {
    if (value <= who.p3) return 3;
    if (value >= who.p97) return 97;
    if (value <= who.p50) return Math.round(3 + (47 * (value - who.p3)) / (who.p50 - who.p3));
    return Math.round(50 + (47 * (value - who.p50)) / (who.p97 - who.p50));
  };

  // ── WHO helper: compute status label for a child value vs WHO ──
  // Green covers the entire WHO healthy band (p3–p97). Only flag truly
  // out-of-range values, with a sub-hint of where in the band the child sits.
  const getPercentileStatus = (
    value: number | null | undefined,
    ageMonths: number,
    metric: 'height' | 'weight' | 'head'
  ): { label: string; color: string; bg: string; percentile: number; severity: 'ok' | 'attention' } | null => {
    if (value === null || value === undefined) return null;
    const ref = childGender === 'girl' ? whoReferenceData.girls : whoReferenceData.boys;
    const who = interpolateWho(ref[metric], ageMonths);
    const percentile = estimatePercentile(value, who);
    if (value < who.p3) {
      return { label: 'Below typical range', color: '#dc2626', bg: '#fef2f2', percentile, severity: 'attention' };
    }
    if (value > who.p97) {
      return { label: 'Above typical range', color: '#7c3aed', bg: '#f5f3ff', percentile, severity: 'attention' };
    }
    return { label: 'Healthy', color: '#059669', bg: '#ecfdf5', percentile, severity: 'ok' };
  };

  // Merged chart data: WHO reference + child's data plotted by age in months
  const mergedGrowthChartData = useMemo(() => {
    const ref = childGender === 'girl' ? whoReferenceData.girls : whoReferenceData.boys;
    const chartType = activeGrowthChart;
    const whoData = ref[chartType];

    // Build base WHO data for months 0-24
    const merged = whoData.map((d) => ({
      month: d.month,
      label: `${d.month}m`,
      p3: d.p3,
      p50: d.p50,
      p97: d.p97,
      childValue: null as number | null,
    }));

    // If DOB is set, overlay child's actual measurements
    if (childDob) {
      const dobTime = new Date(childDob).getTime();
      growthEntries
        .filter((e) => e.date)
        .forEach((e) => {
          const entryTime = new Date(e.date).getTime();
          const ageMonths = Math.round((entryTime - dobTime) / (30.44 * 24 * 60 * 60 * 1000));
          if (ageMonths < 0 || ageMonths > 24) return;

          const val = chartType === 'height'
            ? (e.heightCm !== '' ? Number(e.heightCm) : null)
            : chartType === 'weight'
              ? (e.weightKg !== '' ? Number(e.weightKg) : null)
              : (e.headCircCm !== '' ? Number(e.headCircCm) : null);

          if (val === null) return;

          const slot = merged.find((m) => m.month === ageMonths);
          if (slot) {
            slot.childValue = val;
          } else {
            // Interpolate WHO values for non-exact months
            const who = interpolateWho(whoData, ageMonths);
            merged.push({ month: ageMonths, label: `${ageMonths}m`, p3: who.p3, p50: who.p50, p97: who.p97, childValue: val });
          }
        });

      merged.sort((a, b) => a.month - b.month);
    }

    return merged;
  }, [childGender, activeGrowthChart, childDob, growthEntries]);

  const growthMilestonesCompleted = growthMilestones.filter(
    (m) => growthMilestoneRecords[m.id]?.completed
  ).length;
  const growthMilestonePercent = Math.round(
    (growthMilestonesCompleted / growthMilestones.length) * 100
  );

  const latestGrowth = growthEntries.length > 0
    ? [...growthEntries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
    : null;

  const handleGrowthFormSubmit = () => {
    if (!growthForm.date) return;
    if (growthForm.heightCm === '' && growthForm.weightKg === '' && growthForm.headCircCm === '') return;

    if (editingGrowthId) {
      const editedEntry: GrowthEntry = { ...growthForm, id: editingGrowthId };
      const updated = growthEntries.map((e) =>
        e.id === editingGrowthId ? editedEntry : e
      );
      saveGrowthEntries(updated);
      // Fire API update for edited entry
      if (selectedChildId) {
        const numId = Number(editingGrowthId);
        if (!isNaN(numId)) {
          childDevelopmentService.updateGrowthEntry(selectedChildId, numId, {
            date: growthForm.date, height_cm: growthForm.heightCm, weight_kg: growthForm.weightKg,
            head_circ_cm: growthForm.headCircCm, notes: growthForm.notes,
          }).catch(err => console.error('Failed to update growth entry:', err));
        }
      }
      setEditingGrowthId(null);
    } else {
      const newEntry: GrowthEntry = {
        id: Date.now().toString(),
        ...growthForm,
      };
      saveGrowthEntries([newEntry, ...growthEntries], newEntry);
    }
    setGrowthForm({ date: '', heightCm: '', weightKg: '', headCircCm: '', notes: '' });
    setShowGrowthForm(false);
  };

  const handleEditGrowth = (entry: GrowthEntry) => {
    setGrowthForm({
      date: entry.date,
      heightCm: entry.heightCm,
      weightKg: entry.weightKg,
      headCircCm: entry.headCircCm,
      notes: entry.notes,
    });
    setEditingGrowthId(entry.id);
    setShowGrowthForm(true);
    setTimeout(() => {
      growthFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // ── Child age in months (for milestone auto-select & due date) ──
  const childAgeInMonths = useMemo(() => {
    if (!childDob) return null;
    const dob = new Date(childDob);
    const now = new Date();
    let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
    if (now.getDate() < dob.getDate()) months--;
    return Math.max(months, 0);
  }, [childDob]);

  // ── Auto-select milestone month matching child's current age ──
  useEffect(() => {
    if (milestoneMonthAutoSelected || childAgeInMonths === null) return;
    const monthNums = milestoneMonthKeys.map(Number).sort((a, b) => a - b);
    // Find the closest milestone month <= child's age, or the first one
    let best = monthNums[0];
    for (const m of monthNums) {
      if (m <= childAgeInMonths + 1) best = m;
      else break;
    }
    setSelectedMilestoneMonth(String(best));
    setMilestoneMonthAutoSelected(true);
  }, [childAgeInMonths, milestoneMonthAutoSelected]);

  // ── Due date for selected milestone month ──
  const milestoneDueDate = useMemo(() => {
    if (!childDob) return null;
    const dob = new Date(childDob);
    const monthNum = parseInt(selectedMilestoneMonth, 10);
    const due = new Date(dob.getFullYear(), dob.getMonth() + monthNum, dob.getDate());
    return due;
  }, [childDob, selectedMilestoneMonth]);

  // ── Last updated date for current milestone month ──
  const milestoneLastUpdated = useMemo(() => {
    const items = milestonesByMonth[selectedMilestoneMonth] || [];
    let latestDate: string | null = null;
    items.forEach((catData) => {
      catData.items.forEach((item) => {
        const a = milestoneAssessments[item.id];
        if (a?.date && (!latestDate || a.date > latestDate)) {
          latestDate = a.date;
        }
      });
    });
    return latestDate;
  }, [selectedMilestoneMonth, milestoneAssessments]);

  // ── Milestone month display label ──
  const milestoneMonthLabel = useMemo(() => {
    const num = parseInt(selectedMilestoneMonth, 10);
    if (num === 1) return 'First';
    if (num === 2) return 'Second';
    if (num === 3) return 'Third';
    if (num === 4) return 'Fourth';
    const suffixes: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };
    const suffix = suffixes[num % 10] && num % 100 !== 11 && num % 100 !== 12 && num % 100 !== 13 ? suffixes[num % 10] : 'th';
    return `${num}${suffix}`;
  }, [selectedMilestoneMonth]);

  // ── Enhanced Milestone computed values ──
  const currentMonthMilestones = useMemo(() => milestonesByMonth[selectedMilestoneMonth] || [], [selectedMilestoneMonth]);
  const milestoneProgressByCategory = useMemo(() => {
    const result: { [cat: string]: { total: number; yes: number; almost: number; color: string } } = {};
    currentMonthMilestones.forEach((catData) => {
      const total = catData.items.length;
      let yes = 0;
      let almost = 0;
      catData.items.forEach((item) => {
        const a = milestoneAssessments[item.id];
        if (a?.status === 'yes') yes++;
        else if (a?.status === 'almost') almost++;
      });
      result[catData.category] = { total, yes, almost, color: catData.color };
    });
    return result;
  }, [currentMonthMilestones, milestoneAssessments]);

  // ── Milestone Comparison Data (across all months) ──
  const milestoneComparisonData = useMemo(() => {
    const categories = ['Physical', 'Cognitive', 'Communication', 'Social & Emotional'];
    const categoryColors: Record<string, string> = { Physical: '#4DB6AC', Cognitive: '#9575CD', Communication: '#FFB74D', 'Social & Emotional': '#F48FB1' };

    const tableRows = milestoneMonthKeys.map((mk) => {
      const monthData = milestonesByMonth[mk] || [];
      const catStats: Record<string, { yes: number; almost: number; total: number }> = {};
      let overallYes = 0, overallAlmost = 0, overallTotal = 0;

      categories.forEach((cat) => {
        const catGroup = monthData.find((d) => d.category === cat);
        const items = catGroup?.items || [];
        let yes = 0, almost = 0;
        items.forEach((item) => {
          const a = milestoneAssessments[item.id];
          if (a?.status === 'yes') yes++;
          else if (a?.status === 'almost') almost++;
        });
        catStats[cat] = { yes, almost, total: items.length };
        overallYes += yes;
        overallAlmost += almost;
        overallTotal += items.length;
      });

      return { month: mk, catStats, overallYes, overallAlmost, overallTotal };
    });

    const chartData = tableRows.map((row) => ({
      month: `${row.month}m`,
      Yes: row.overallYes,
      Almost: row.overallAlmost,
      'Not Done': row.overallTotal - row.overallYes - row.overallAlmost,
    }));

    return { tableRows, chartData, categories, categoryColors };
  }, [milestoneAssessments]);

  // ── Growth summary cards (At Birth / Current / Change) ──
  const growthSummary = useMemo(() => {
    const sorted = [...growthEntries].filter((e) => e.date).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const first = sorted.length > 0 ? sorted[0] : null;
    const latest = sorted.length > 0 ? sorted[sorted.length - 1] : null;
    return {
      height: {
        birth: first?.heightCm !== '' && first?.heightCm !== undefined ? Number(first.heightCm) : null,
        current: latest?.heightCm !== '' && latest?.heightCm !== undefined ? Number(latest.heightCm) : null,
      },
      weight: {
        birth: first?.weightKg !== '' && first?.weightKg !== undefined ? Number(first.weightKg) : null,
        current: latest?.weightKg !== '' && latest?.weightKg !== undefined ? Number(latest.weightKg) : null,
      },
      head: {
        birth: first?.headCircCm !== '' && first?.headCircCm !== undefined ? Number(first.headCircCm) : null,
        current: latest?.headCircCm !== '' && latest?.headCircCm !== undefined ? Number(latest.headCircCm) : null,
      },
    };
  }, [growthEntries]);

  // ── Feeding computed for today's summary ──
  const feedingTodayEntries = feedingEntries.filter((e) => e.date === feedingDate);
  const feedingTodayBreastCount = feedingTodayEntries.filter((e) => e.type === 'breast').length;
  const feedingTodayBottleCount = feedingTodayEntries.filter((e) => e.type === 'bottle').length;
  const feedingTodayBottleMl = feedingTodayEntries.filter((e) => e.type === 'bottle').reduce((sum, e) => sum + (e.amountMl ? Number(e.amountMl) : 0), 0);
  const feedingTodaySolidsCount = feedingTodayEntries.filter((e) => e.type === 'solid').length;
  const diaperTodayEntries = diaperEntries.filter((e) => e.date === feedingDate);
  const diaperTodayWet = diaperTodayEntries.filter((e) => e.type === 'wet' || e.type === 'both').length;
  const diaperTodayPoop = diaperTodayEntries.filter((e) => e.type === 'poop' || e.type === 'both').length;

  // ── Child age display ──
  const childAgeText = useMemo(() => {
    if (!childDob) return '';
    const dob = new Date(childDob);
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();
    if (months < 0) { years--; months += 12; }
    if (now.getDate() < dob.getDate()) {
      months--;
      if (months < 0) { years--; months += 12; }
    }
    if (years > 0 && months > 0) return `${years} yr ${months} mo`;
    if (years > 0) return `${years} yr`;
    if (months > 0) return `${months} mo`;
    const days = Math.floor((now.getTime() - dob.getTime()) / (24 * 60 * 60 * 1000));
    if (days >= 0) return `${days} days`;
    return '';
  }, [childDob]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={`child-development-page theme-${childGender}`}>
      {/* ── Migration Prompt Banner ── */}
      {showMigrationPrompt && (
        <div className="cd-migration-banner" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff',
          padding: '20px 24px', borderRadius: 12, margin: '16px auto', maxWidth: 960, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t('childDev:migration.title')}</h3>
            <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: 14 }}>
              {t('childDev:migration.description')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleMigration}
              disabled={migrating}
              style={{
                background: '#fff', color: '#764ba2', border: 'none', borderRadius: 8, padding: '10px 24px',
                fontWeight: 600, cursor: migrating ? 'not-allowed' : 'pointer', opacity: migrating ? 0.7 : 1,
                fontSize: 14, transition: 'all 0.2s',
              }}
            >
              {migrating ? t('childDev:migration.migrating') : t('childDev:migration.migrateNow')}
            </button>
            <button
              onClick={() => setShowMigrationPrompt(false)}
              style={{
                background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 8, padding: '10px 20px', fontWeight: 500, cursor: 'pointer', fontSize: 14,
              }}
            >
              {t('childDev:migration.later')}
            </button>
          </div>
        </div>
      )}

      {/* ── Children Switcher Modal Dialog ── */}
      {showChildrenDialog && children.length >= 1 && (
        <div
          className="cd-children-modal-overlay"
          onClick={() => { setShowChildrenDialog(false); setShowAddChildForm(false); }}
          role="presentation"
        >
          <div
            className="cd-children-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cd-children-modal-title"
          >
            <header className="cd-children-modal-header">
              <div>
                <h3 id="cd-children-modal-title" className="cd-children-modal-title">
                  <FontAwesomeIcon icon={faUsers} /> {t('childDev:myChildren', { defaultValue: 'My Children' })}
                </h3>
                <span className="cd-children-modal-sub">
                  {children.length === 1
                    ? t('childDev:oneChildTracked', { defaultValue: '1 child being tracked' })
                    : t('childDev:multipleChildrenTracked', { defaultValue: `${children.length} children being tracked`, count: children.length })}
                </span>
              </div>
              <button
                type="button"
                className="cd-children-modal-close"
                onClick={() => { setShowChildrenDialog(false); setShowAddChildForm(false); }}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </header>

            <div className="cd-children-modal-body">
              <div className="cd-children-grid">
                {children.map(child => {
                  const isActive = selectedChildId === child.id;
                  const initial = (child.name || '?').trim().charAt(0).toUpperCase();
                  const dob = child.dob;
                  let ageLabel = '';
                  if (dob) {
                    const d = new Date(dob);
                    const now = new Date();
                    let yrs = now.getFullYear() - d.getFullYear();
                    let mos = now.getMonth() - d.getMonth();
                    if (now.getDate() < d.getDate()) mos--;
                    if (mos < 0) { yrs--; mos += 12; }
                    ageLabel = yrs > 0 ? `${yrs} yr${yrs > 1 ? 's' : ''}${mos > 0 ? ` ${mos} mo` : ''}` : `${mos} mo`;
                  }
                  const genderClass = child.gender === 'girl' ? 'girl' : child.gender === 'boy' ? 'boy' : 'neutral';
                  return (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => { setSelectedChildId(child.id); setShowChildrenDialog(false); }}
                      className={`cd-child-chip ${genderClass} ${isActive ? 'active' : ''}`}
                      aria-pressed={isActive}
                    >
                      <span className="cd-child-chip-avatar">{initial}</span>
                      <span className="cd-child-chip-meta">
                        <span className="cd-child-chip-name">{child.name || t('childDev:unnamed', { defaultValue: 'Unnamed child' })}</span>
                        {ageLabel && <span className="cd-child-chip-age"><FontAwesomeIcon icon={faCalendarAlt} /> {ageLabel}</span>}
                      </span>
                      {isActive && (
                        <span className="cd-child-chip-active-dot" aria-hidden="true">
                          <FontAwesomeIcon icon={faCircleCheck} />
                        </span>
                      )}
                    </button>
                  );
                })}
                {!showAddChildForm && (
                  <button
                    type="button"
                    className="cd-child-chip cd-child-chip-add"
                    onClick={() => setShowAddChildForm(true)}
                  >
                    <span className="cd-child-chip-avatar cd-child-chip-avatar-add">
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                    <span className="cd-child-chip-meta">
                      <span className="cd-child-chip-name">{t('childDev:addChild', { defaultValue: 'Add a child' })}</span>
                      <span className="cd-child-chip-age cd-child-chip-add-hint">
                        {t('childDev:trackAnother', { defaultValue: 'Track another' })}
                      </span>
                    </span>
                  </button>
                )}
              </div>

              {showAddChildForm && (
                <div className="cd-add-child-form-inline">
                  <div className="cd-add-child-form-grid">
                    <div className="cd-add-child-field">
                      <label htmlFor="cd-new-child-name">{t('childDev:name')}</label>
                      <input
                        id="cd-new-child-name"
                        type="text"
                        placeholder={t('childDev:enterNamePlaceholder')}
                        value={newChildForm.name}
                        onChange={e => setNewChildForm({ ...newChildForm, name: e.target.value })}
                        autoFocus
                      />
                    </div>
                    <div className="cd-add-child-field">
                      <label htmlFor="cd-new-child-dob">{t('childDev:dateOfBirth')}</label>
                      <input
                        id="cd-new-child-dob"
                        type="date"
                        value={newChildForm.dob}
                        onChange={e => setNewChildForm({ ...newChildForm, dob: e.target.value })}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="cd-add-child-field">
                      <label>{t('childDev:gender', { defaultValue: 'Gender' })}</label>
                      <div className="cd-add-child-gender-row">
                        {(['boy', 'girl', 'neutral'] as const).map(g => (
                          <button
                            key={g}
                            type="button"
                            className={`cd-add-child-gender-btn ${g} ${newChildForm.gender === g ? 'active' : ''}`}
                            onClick={() => setNewChildForm({ ...newChildForm, gender: g })}
                          >
                            {g === 'boy' ? t('childDev:boy') : g === 'girl' ? t('childDev:girl') : t('childDev:neutral')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="cd-add-child-form-actions">
                    <button
                      type="button"
                      className="cd-add-child-cancel"
                      onClick={() => { setShowAddChildForm(false); setNewChildForm({ name: '', gender: 'neutral', dob: '' }); }}
                    >
                      {t('childDev:cancel')}
                    </button>
                    <button
                      type="button"
                      className="cd-add-child-submit"
                      onClick={handleAddChild}
                      disabled={!newChildForm.name || !newChildForm.dob}
                    >
                      <FontAwesomeIcon icon={faPlus} /> {t('childDev:add')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Loading State ── */}
      {(childrenLoading || dataLoading) && (
        <div style={{
          maxWidth: 960, margin: '40px auto', textAlign: 'center', padding: 60,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
            animation: 'cdBabyBounce 1.2s ease-in-out infinite',
          }}>
            <FontAwesomeIcon icon={faBaby} style={{ fontSize: 32, color: '#6366f1' }} />
          </div>
          <p style={{ color: '#64748b', fontSize: 16, fontWeight: 500, margin: 0 }}>
            {childrenLoading ? t('childDev:loadingChildren') : t('childDev:loadingChildData')}
          </p>
          <style>{`@keyframes cdBabyBounce { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-8px) scale(1.05); } }`}</style>
        </div>
      )}

      {/* ── Welcome Banner ── */}
      <section className="cd-welcome-banner">
        <Container>
          <BackButton />
          <div className="cd-welcome-layout">
            <div className="cd-welcome-text">
              {selectedChildId && (childName || childDob) ? (
                <div className="cd-hero-child-profile">
                  <div className="cd-hero-child-info">
                    {childName && <h1 className="cd-welcome-title"><FontAwesomeIcon icon={faBaby} /> {childName}</h1>}
                    {!childName && <h1 className="cd-welcome-title"><FontAwesomeIcon icon={faBaby} /> {t('childDev:yourLittleOne')}</h1>}
                    {childAgeText && (
                      <span className="cd-hero-child-age">
                        <FontAwesomeIcon icon={faCalendarAlt} /> {childAgeText} {t('childDev:old')}
                      </span>
                    )}
                  </div>
                  <div className="cd-hero-actions">
                    {children.length >= 1 && (
                      <button
                        className="cd-hero-children-btn"
                        onClick={() => setShowChildrenDialog(true)}
                        aria-label={t('childDev:myChildren', { defaultValue: 'My Children' })}
                      >
                        <FontAwesomeIcon icon={faUsers} />
                        <span className="cd-hero-children-btn-label">
                          {t('childDev:myChildren', { defaultValue: 'My Children' })}
                        </span>
                        {children.length > 1 && (
                          <span className="cd-hero-children-btn-count">{children.length}</span>
                        )}
                      </button>
                    )}
                    <button
                      className="cd-hero-edit-profile-btn"
                      onClick={() => setShowChildProfileEdit(!showChildProfileEdit)}
                      aria-label="Edit child profile"
                    >
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="cd-welcome-title">
                    <FontAwesomeIcon icon={faBaby} />
                    {t('childDev:title')}
                  </h1>
                  <p className="cd-welcome-subtitle">
                    {user?.name ? `Welcome, ${user.name}!` : 'Welcome!'} {t('childDev:welcome.subtitle')}
                  </p>
                </>
              )}
              {/* Inline child profile edit */}
              {!selectedChildId || showChildProfileEdit ? (
                <div className="cd-hero-profile-form">
                  <div className="cd-hero-profile-inputs">
                    <div className="cd-hero-profile-field">
                      <label htmlFor="child-name-input">{t('childDev:childName')}</label>
                      <input
                        id="child-name-input"
                        type="text"
                        placeholder={t('childDev:enterName')}
                        value={childName}
                        onChange={(e) => {
                          setChildName(e.target.value);
                          updateChildProfile('name', e.target.value);
                        }}
                      />
                    </div>
                    <div className="cd-hero-profile-field">
                      <label htmlFor="child-dob-input">{t('childDev:dateOfBirth')}</label>
                      <input
                        id="child-dob-input"
                        type="date"
                        value={childDob}
                        onChange={(e) => {
                          setChildDob(e.target.value);
                          updateChildProfile('dob', e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  {profileFormError && (
                    <p style={{ color: '#ef4444', fontSize: 13, margin: '8px 0 0' }}>{profileFormError}</p>
                  )}
                  {(showChildProfileEdit || !selectedChildId) && (
                    <button
                      className="cd-hero-profile-done-btn"
                      onClick={async () => {
                        if (!selectedChildId) {
                          if (!childName && !childDob) {
                            setProfileFormError(t('childDev:validation.enterNameAndDob'));
                            return;
                          }
                          if (!childName) {
                            setProfileFormError(t('childDev:validation.enterChildName'));
                            return;
                          }
                          if (!childDob) {
                            setProfileFormError(t('childDev:validation.enterDob'));
                            return;
                          }
                          setProfileFormError('');
                          try {
                            const child = await childDevelopmentService.createChild({
                              name: childName,
                              gender: childGender,
                              dob: childDob,
                            });
                            setChildren(prev => [...prev, child]);
                            setSelectedChildId(child.id);
                          } catch (err) {
                            console.error('Failed to create child:', err);
                            return;
                          }
                        }
                        setShowChildProfileEdit(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faCheck} /> {t('childDev:done')}
                    </button>
                  )}
                </div>
              ) : null}
              <div className="cd-theme-switcher-row">
                <span className="cd-theme-switcher-label">{t('childDev:theme')}:</span>
                <div className="cd-theme-switcher" role="radiogroup" aria-label="Color theme">
                  <button
                    className={`cd-theme-btn ${childGender === 'boy' ? 'active' : ''}`}
                    onClick={() => { setChildGender('boy'); updateChildProfile('gender', 'boy'); }}
                    role="radio"
                    aria-checked={childGender === 'boy'}
                    aria-label="Boy theme"
                  >
                    <span className="cd-theme-btn-icon" aria-hidden="true">&#9794;</span>
                    {t('childDev:boy')}
                  </button>
                  <button
                    className={`cd-theme-btn ${childGender === 'girl' ? 'active' : ''}`}
                    onClick={() => { setChildGender('girl'); updateChildProfile('gender', 'girl'); }}
                    role="radio"
                    aria-checked={childGender === 'girl'}
                    aria-label="Girl theme"
                  >
                    <span className="cd-theme-btn-icon" aria-hidden="true">&#9792;</span>
                    {t('childDev:girl')}
                  </button>
                  <button
                    className={`cd-theme-btn ${childGender === 'neutral' ? 'active' : ''}`}
                    onClick={() => { setChildGender('neutral'); updateChildProfile('gender', 'neutral'); }}
                    role="radio"
                    aria-checked={childGender === 'neutral'}
                    aria-label="Neutral theme"
                  >
                    <span className="cd-theme-btn-icon" aria-hidden="true">&#9734;</span>
                    {t('childDev:neutral')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Stats Row ── */}
      <section className="cd-stats-section">
        <Container>
          <div className="cd-stats-row">
            <div className="cd-stat-card">
              <div className="cd-stat-icon" style={{ background: '#06b6d412', color: '#06b6d4' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="cd-stat-info">
                <span className="cd-stat-value">{growthEntries.length}</span>
                <span className="cd-stat-label">{t('childDev:stats.growthLogs')}</span>
              </div>
            </div>
            <div className="cd-stat-card">
              <div className="cd-stat-icon" style={{ background: '#f59e0b12', color: '#f59e0b' }}>
                <FontAwesomeIcon icon={faCamera} />
              </div>
              <div className="cd-stat-info">
                <span className="cd-stat-value">{memories.length}</span>
                <span className="cd-stat-label">{t('childDev:stats.memories')}</span>
              </div>
            </div>
            <div className="cd-stat-card">
              <div className="cd-stat-icon" style={{ background: '#10b98112', color: '#10b981' }}>
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="cd-stat-info">
                <span className="cd-stat-value">{vaccCompleted}</span>
                <span className="cd-stat-label">{t('childDev:stats.vaccinations')}</span>
              </div>
            </div>
            <div className="cd-stat-card">
              <div className="cd-stat-icon" style={{ background: '#8b5cf612', color: '#8b5cf6' }}>
                <FontAwesomeIcon icon={faTooth} />
              </div>
              <div className="cd-stat-info">
                <span className="cd-stat-value">{teethAppeared}</span>
                <span className="cd-stat-label">{t('childDev:stats.teeth')}</span>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Tab Navigation ── */}
      <Container>
        <div className="cd-tabs-container" role="tablist" aria-label="Child development sections">
          {tabConfig.map((tab) => (
            <button
              key={tab.key}
              className={`cd-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              style={activeTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : {}}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-label={tab.label}
            >
              <FontAwesomeIcon icon={tab.icon} className="cd-tab-icon" />
              {tab.label}
            </button>
          ))}
        </div>
      </Container>

      {/* ── Tab Content ── */}
      <Container>
        <div className="cd-tab-content" role="tabpanel">

          {/* ══════════ ACTIVITIES TAB ══════════ */}
          {activeTab === 'Activities' && (
            <div className="cd-activities-tab">
              <div className="cd-tab-header cd-tab-header-activities">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faBullseye} className="cd-tab-header-icon cd-icon-activities" />
                    {t('childDev:activities.title')}
                  </h2>
                  <p>{t('childDev:activities.description')}</p>
                </div>
                <div className="cd-tab-header-img">
                  <img src="/Fitness-Profile.png" alt="Activities" />
                </div>
              </div>
              <div className="cd-activity-grid">
                {activityCategories.map((cat) => {
                  const completedCount = cat.activities.filter(
                    (a) => activityRecords[`${cat.group}__${a}`]
                  ).length;
                  const isExpanded = expandedGroup === cat.group;

                  return (
                    <div
                      key={cat.group}
                      className={`cd-activity-card ${isExpanded ? 'expanded' : ''}`}
                      style={{ '--activity-color': cat.color } as React.CSSProperties}
                    >
                      <div
                        className="cd-activity-card-header"
                        onClick={() => setExpandedGroup(isExpanded ? null : cat.group)}
                        role="button"
                        aria-expanded={isExpanded}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedGroup(isExpanded ? null : cat.group); }}}
                      >
                        <div className="cd-activity-card-header-left">
                          <span className="cd-activity-icon-wrap" style={{ background: `${cat.color}15`, color: cat.color }}>
                            <FontAwesomeIcon icon={cat.icon} />
                          </span>
                          <div>
                            <h3 className="cd-activity-group-name">{cat.group}</h3>
                            <span className="cd-activity-count">
                              {completedCount}/{cat.activities.length} {t('childDev:activities.completed')}
                            </span>
                          </div>
                        </div>
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="cd-activity-toggle-icon"
                        />
                      </div>
                      <div className="cd-activity-progress-bar">
                        <div
                          className="cd-activity-progress-fill"
                          style={{
                            width: `${(completedCount / cat.activities.length) * 100}%`,
                            background: `linear-gradient(90deg, ${cat.color}, ${cat.color}88)`,
                          }}
                        />
                      </div>
                      {isExpanded && (
                        <div className="cd-activity-list">
                          {cat.activities.map((activity) => {
                            const key = `${cat.group}__${activity}`;
                            const isChecked = !!activityRecords[key];
                            return (
                              <label key={key} className="cd-activity-item">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    const updated = { ...activityRecords, [key]: !isChecked };
                                    saveActivities(updated, key);
                                  }}
                                />
                                <span className={`cd-activity-name ${isChecked ? 'completed' : ''}`}>
                                  {activity}
                                </span>
                                {isChecked && (
                                  <FontAwesomeIcon icon={faCheck} className="cd-activity-check" />
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════════ GROWTH TAB ══════════ */}
          {activeTab === 'Growth' && (
            <div className="cd-growth-tab">
              <div className="cd-tab-header cd-tab-header-growth">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faChartLine} className="cd-tab-header-icon cd-icon-growth" />
                    {t('childDev:growth.title')}
                  </h2>
                  <p>{t('childDev:growth.description')}</p>
                </div>
              </div>

              {/* Growth Entry Form */}
              <div className="cd-growth-header">
                <h2 className="cd-section-title">
                  <FontAwesomeIcon icon={faChartLine} className="cd-title-icon cd-icon-growth" />
                  {t('childDev:growth.growthLog')}
                </h2>
                <button
                  className="cd-add-btn"
                  onClick={() => {
                    setShowGrowthForm(!showGrowthForm);
                    if (showGrowthForm) {
                      setEditingGrowthId(null);
                      setGrowthForm({ date: '', heightCm: '', weightKg: '', headCircCm: '', notes: '' });
                    }
                  }}
                  aria-label={showGrowthForm ? 'Cancel' : 'Add growth entry'}
                >
                  <FontAwesomeIcon icon={showGrowthForm ? faTimes : faPlus} />
                  {showGrowthForm ? t('childDev:cancel') : t('childDev:growth.addEntry')}
                </button>
              </div>

              {showGrowthForm && (
                <div className="cd-growth-form-card" ref={growthFormRef}>
                  <h3>{editingGrowthId ? t('childDev:growth.editGrowthEntry') : t('childDev:growth.newGrowthEntry')}</h3>
                  <div className="cd-growth-form-grid">
                    <div className="cd-form-group">
                      <label htmlFor="growth-date">{t('childDev:growth.date')}</label>
                      <input
                        id="growth-date"
                        type="date"
                        value={growthForm.date}
                        onChange={(e) => setGrowthForm({ ...growthForm, date: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="growth-height">{t('childDev:growth.heightCm')}</label>
                      <input
                        id="growth-height"
                        type="number"
                        step="0.1"
                        min="0"
                        max="200"
                        placeholder="e.g. 65.5"
                        value={growthForm.heightCm}
                        onChange={(e) => setGrowthForm({ ...growthForm, heightCm: e.target.value ? parseFloat(e.target.value) : '' })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="growth-weight">{t('childDev:growth.weightKg')}</label>
                      <input
                        id="growth-weight"
                        type="number"
                        step="0.1"
                        min="0"
                        max="50"
                        placeholder="e.g. 7.2"
                        value={growthForm.weightKg}
                        onChange={(e) => setGrowthForm({ ...growthForm, weightKg: e.target.value ? parseFloat(e.target.value) : '' })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="growth-head">{t('childDev:growth.headCircCm')}</label>
                      <input
                        id="growth-head"
                        type="number"
                        step="0.1"
                        min="0"
                        max="60"
                        placeholder="e.g. 42.0"
                        value={growthForm.headCircCm}
                        onChange={(e) => setGrowthForm({ ...growthForm, headCircCm: e.target.value ? parseFloat(e.target.value) : '' })}
                      />
                    </div>
                    <div className="cd-form-group cd-form-full-width">
                      <label htmlFor="growth-notes">{t('childDev:growth.notesOptional')}</label>
                      <input
                        id="growth-notes"
                        type="text"
                        placeholder="e.g. Doctor visit, checkup"
                        value={growthForm.notes}
                        onChange={(e) => setGrowthForm({ ...growthForm, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="cd-growth-form-actions">
                    <button className="cd-btn-primary" onClick={handleGrowthFormSubmit}>
                      {editingGrowthId ? t('childDev:growth.updateEntry') : t('childDev:growth.saveEntry')}
                    </button>
                    <button
                      className="cd-btn-secondary"
                      onClick={() => {
                        setShowGrowthForm(false);
                        setEditingGrowthId(null);
                        setGrowthForm({ date: '', heightCm: '', weightKg: '', headCircCm: '', notes: '' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Combined growth overview — progression (At Birth → Current → Change) + WHO benchmark */}
              {growthEntries.length > 0 && latestGrowth && (() => {
                const dobTime = childDob ? new Date(childDob).getTime() : null;
                const latestTime = new Date(latestGrowth.date).getTime();
                const ageMonthsLatest = dobTime !== null
                  ? Math.round((latestTime - dobTime) / (30.44 * 24 * 60 * 60 * 1000))
                  : null;
                const canShowWho = ageMonthsLatest !== null && ageMonthsLatest >= 0 && ageMonthsLatest <= 24;
                const ref = childGender === 'girl' ? whoReferenceData.girls : whoReferenceData.boys;
                const metrics = [
                  {
                    key: 'height', label: t('childDev:growth.height'), unit: 'cm',
                    icon: faRulerVertical, color: '#6366f1',
                    birth: growthSummary.height.birth, current: growthSummary.height.current,
                    who: canShowWho ? interpolateWho(ref.height, ageMonthsLatest as number) : null,
                  },
                  {
                    key: 'weight', label: t('childDev:growth.weight'), unit: 'kg',
                    icon: faWeight, color: '#059669',
                    birth: growthSummary.weight.birth, current: growthSummary.weight.current,
                    who: canShowWho ? interpolateWho(ref.weight, ageMonthsLatest as number) : null,
                  },
                  {
                    key: 'head', label: t('childDev:growth.headCirc'), unit: 'cm',
                    icon: faCircle, color: '#f59e0b',
                    birth: growthSummary.head.birth, current: growthSummary.head.current,
                    who: canShowWho ? interpolateWho(ref.head, ageMonthsLatest as number) : null,
                  },
                ];
                return (
                  <div className="cd-growth-status-panel">
                    <div className="cd-growth-status-panel-header">
                      <span className="cd-growth-status-panel-title">
                        <FontAwesomeIcon icon={faBaby} /> {t('childDev:growth.babyStatus')}
                        {canShowWho && (
                          <span className="cd-growth-status-age-badge">
                            {ageMonthsLatest} {t('childDev:growth.monthsOld')}
                          </span>
                        )}
                      </span>
                      <span className="cd-growth-status-panel-date">
                        {t('childDev:growth.lastMeasured')} {new Date(latestGrowth.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="cd-growth-status-cards">
                      {metrics.map((m) => {
                        const change = (m.birth !== null && m.current !== null)
                          ? m.current - m.birth
                          : null;
                        const status = (m.current !== null && m.who) ? (
                          m.current < m.who.p3  ? { label: 'Below Normal', color: '#dc2626', bg: '#fef2f2', bar: 10 } :
                          m.current < m.who.p50 ? { label: 'Below Ideal',  color: '#d97706', bg: '#fffbeb', bar: 40 } :
                          m.current <= m.who.p97 ? { label: '✓ Healthy',   color: '#059669', bg: '#ecfdf5', bar: 75 } :
                          { label: 'Above Normal', color: '#7c3aed', bg: '#f5f3ff', bar: 95 }
                        ) : null;
                        return (
                          <div key={m.key} className="cd-growth-status-card">
                            <div className="cd-growth-status-card-header" style={{ color: m.color }}>
                              <FontAwesomeIcon icon={m.icon} />
                              <span>{m.label}</span>
                            </div>

                            {/* Progression: At Birth → Current → Change */}
                            <div className="cd-growth-progression">
                              <div className="cd-growth-progression-col">
                                <span className="cd-growth-progression-label">{t('childDev:growth.atBirth')}</span>
                                <span className="cd-growth-progression-val">
                                  {m.birth !== null ? `${m.birth} ${m.unit}` : '--'}
                                </span>
                              </div>
                              <div className="cd-growth-progression-col">
                                <span className="cd-growth-progression-label">{t('childDev:growth.current')}</span>
                                <span className="cd-growth-progression-val cd-growth-progression-current" style={{ color: m.color }}>
                                  {m.current !== null ? `${m.current} ${m.unit}` : '--'}
                                </span>
                              </div>
                              <div className="cd-growth-progression-col">
                                <span className="cd-growth-progression-label">{t('childDev:growth.change')}</span>
                                <span className="cd-growth-progression-val cd-gs-change">
                                  {change !== null ? `+${change.toFixed(1)} ${m.unit}` : '--'}
                                </span>
                              </div>
                            </div>

                            {/* WHO benchmark — only when age falls in 0–24 mo */}
                            {m.who && m.current !== null ? (
                              <>
                                <div className="cd-growth-status-ideal">
                                  <span className="cd-growth-status-ideal-label">{t('childDev:growth.ideal50th')}</span>
                                  <span className="cd-growth-status-ideal-val">{m.who.p50} {m.unit}</span>
                                </div>
                                <div className="cd-growth-status-range">
                                  <span className="cd-growth-status-range-label">{t('childDev:growth.normalRange')}</span>
                                  <span className="cd-growth-status-range-val">{m.who.p3}–{m.who.p97} {m.unit}</span>
                                </div>
                                <div className="cd-growth-status-bar-wrap">
                                  <div className="cd-growth-status-bar-track">
                                    <div className="cd-growth-status-bar-fill" style={{ width: `${status?.bar ?? 50}%`, background: status?.color ?? m.color }} />
                                    <div className="cd-growth-status-bar-marker" style={{ left: '10%' }} title="3rd Percentile" />
                                    <div className="cd-growth-status-bar-marker" style={{ left: '50%' }} title="50th Percentile" />
                                    <div className="cd-growth-status-bar-marker" style={{ left: '90%' }} title="97th Percentile" />
                                  </div>
                                  <div className="cd-growth-status-bar-labels">
                                    <span>3rd</span><span>50th</span><span>97th</span>
                                  </div>
                                </div>
                                {status && (
                                  <span className="cd-growth-status-badge" style={{ color: status.color, background: status.bg }}>
                                    {status.label}
                                  </span>
                                )}
                              </>
                            ) : m.current === null ? (
                              <span className="cd-growth-status-na">{t('childDev:growth.notRecorded')}</span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Growth Charts with WHO Reference */}
              <div className="cd-growth-charts-section">
                <div className="cd-growth-chart-controls">
                  <div className="cd-growth-chart-tabs">
                    <button
                      className={`cd-growth-chart-tab ${activeGrowthChart === 'height' ? 'active' : ''}`}
                      onClick={() => setActiveGrowthChart('height')}
                    >
                      <FontAwesomeIcon icon={faRulerVertical} /> {t('childDev:growth.height')}
                    </button>
                    <button
                      className={`cd-growth-chart-tab ${activeGrowthChart === 'weight' ? 'active' : ''}`}
                      onClick={() => setActiveGrowthChart('weight')}
                    >
                      <FontAwesomeIcon icon={faWeight} /> {t('childDev:growth.weight')}
                    </button>
                    <button
                      className={`cd-growth-chart-tab ${activeGrowthChart === 'head' ? 'active' : ''}`}
                      onClick={() => setActiveGrowthChart('head')}
                    >
                      <FontAwesomeIcon icon={faCircle} /> {t('childDev:growth.headCirc')}
                    </button>
                  </div>
                  <div className="cd-growth-gender-toggle">
                    <button
                      className={`cd-gender-btn ${childGender === 'boy' ? 'active boy' : ''}`}
                      onClick={() => { setChildGender('boy'); updateChildProfile('gender', 'boy'); }}
                    >
                      {t('childDev:boy')}
                    </button>
                    <button
                      className={`cd-gender-btn ${childGender === 'girl' ? 'active girl' : ''}`}
                      onClick={() => { setChildGender('girl'); updateChildProfile('gender', 'girl'); }}
                    >
                      {t('childDev:girl')}
                    </button>
                    <button
                      className={`cd-gender-btn ${childGender === 'neutral' ? 'active neutral' : ''}`}
                      onClick={() => { setChildGender('neutral'); updateChildProfile('gender', 'neutral'); }}
                    >
                      {t('childDev:neutral')}
                    </button>
                  </div>
                </div>

                {!childDob && (
                  <div className="cd-growth-dob-prompt">
                    <p>{t('childDev:growth.dobPrompt')}</p>
                    <div className="cd-growth-dob-input-row">
                      <label htmlFor="growth-child-dob">{t('childDev:dateOfBirth')}:</label>
                      <input
                        id="growth-child-dob"
                        type="date"
                        value={childDob}
                        onChange={(e) => {
                          setChildDob(e.target.value);
                          updateChildProfile('dob', e.target.value);
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* "Where Your Child Stands Today" — at-a-glance WHO comparison from latest entry */}
                {childDob && latestGrowth && (() => {
                  const latestAgeM = getAgeMonths(latestGrowth.date);
                  if (latestAgeM === null || latestAgeM < 0 || latestAgeM > 24) return null;
                  const hVal = latestGrowth.heightCm !== '' ? Number(latestGrowth.heightCm) : null;
                  const wVal = latestGrowth.weightKg !== '' ? Number(latestGrowth.weightKg) : null;
                  const hdVal = latestGrowth.headCircCm !== '' ? Number(latestGrowth.headCircCm) : null;
                  const hSt = getPercentileStatus(hVal, latestAgeM, 'height');
                  const wSt = getPercentileStatus(wVal, latestAgeM, 'weight');
                  const hdSt = getPercentileStatus(hdVal, latestAgeM, 'head');
                  const tiles = [
                    { key: 'height', label: 'Height', val: hVal, unit: 'cm', icon: faRulerVertical, st: hSt, ideal: hVal !== null ? getWhoIdeal(latestAgeM, 'height') : null },
                    { key: 'weight', label: 'Weight', val: wVal, unit: 'kg', icon: faWeight, st: wSt, ideal: wVal !== null ? getWhoIdeal(latestAgeM, 'weight') : null },
                    { key: 'head', label: 'Head', val: hdVal, unit: 'cm', icon: faCircle, st: hdSt, ideal: hdVal !== null ? getWhoIdeal(latestAgeM, 'head') : null },
                  ].filter(tile => tile.val !== null && tile.st !== null);
                  if (tiles.length === 0) return null;
                  return (
                    <div className="cd-who-standing-card">
                      <div className="cd-who-standing-header">
                        <div>
                          <h4>Where Your Child Stands Today</h4>
                          <span className="cd-who-standing-sub">
                            Based on entry from {new Date(latestGrowth.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · Age {latestAgeM} months · WHO {childGender === 'girl' ? 'girls' : 'boys'} standards
                          </span>
                        </div>
                      </div>
                      <div className="cd-who-standing-tiles">
                        {tiles.map(tile => (
                          <div key={tile.key} className="cd-who-standing-tile" style={{ borderLeft: `4px solid ${tile.st!.color}` }}>
                            <div className="cd-who-standing-tile-top">
                              <FontAwesomeIcon icon={tile.icon} style={{ color: tile.st!.color }} />
                              <span className="cd-who-standing-tile-label">{tile.label}</span>
                            </div>
                            <div className="cd-who-standing-tile-value">
                              {tile.val} <span className="cd-who-standing-unit">{tile.unit}</span>
                            </div>
                            <div className="cd-who-standing-tile-ideal">
                              Average for age: <strong>{tile.ideal} {tile.unit}</strong>
                            </div>
                            <div className="cd-who-standing-tile-status" style={{ color: tile.st!.color, background: tile.st!.bg }}>
                              {tile.st!.severity === 'ok'
                                ? <>✓ {tile.st!.label} <span className="cd-who-standing-pct">~{tile.st!.percentile}th percentile</span></>
                                : <>⚠ {tile.st!.label} <span className="cd-who-standing-pct">Talk to your paediatrician</span></>}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="cd-who-standing-foot">
                        <FontAwesomeIcon icon={faCircleInfo} /> Percentiles compare your child to other healthy children of the same age and gender. Anywhere between the 3rd and 97th is normal — being closer to the average is not necessarily better.
                      </div>
                    </div>
                  );
                })()}

                <div className="cd-growth-chart-container">
                  <ResponsiveContainer width="100%" height={380}>
                    <AreaChart data={mergedGrowthChartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="whoBandGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.12} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                        label={{ value: 'Age (months)', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#94a3b8' }}
                      />
                      <YAxis
                        unit={activeGrowthChart === 'weight' ? ' kg' : ' cm'}
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                        domain={['dataMin - 2', 'dataMax + 2']}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: '14px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '12px 16px', fontSize: '0.85rem', background: '#fff' }}
                        labelStyle={{ fontWeight: 600, color: '#1e293b', marginBottom: 6, fontSize: '0.9rem' }}
                        labelFormatter={(label) => `Age: ${label}`}
                        formatter={(value: number | undefined, name?: string, _item?: any, _idx?: number, payload?: any) => {
                          if (value === null || value === undefined || value === 0) return [null, null];
                          const unit = activeGrowthChart === 'weight' ? 'kg' : 'cm';
                          if (name === 'childValue' && payload) {
                            const p = payload as any;
                            let status = '';
                            if (p.p3 && p.p50 && p.p97) {
                              const pct = estimatePercentile(value, { p3: p.p3, p50: p.p50, p97: p.p97 });
                              if (value < p.p3) status = ' — Below typical range';
                              else if (value > p.p97) status = ' — Above typical range';
                              else status = ` — Healthy (~${pct}th percentile)`;
                            }
                            return [`${value} ${unit}${status}`, '⭐ Your Child'];
                          }
                          const labels: Record<string, string> = {
                            p97: 'Upper end of healthy range',
                            p50: 'Average for age',
                            p3: 'Lower end of healthy range',
                          };
                          return [`${value} ${unit}`, labels[name || ''] || name || ''];
                        }}
                        itemSorter={(item) => {
                          const order: Record<string, number> = { childValue: 0, p50: 1, p97: 2, p3: 3 };
                          return order[item.dataKey as string] ?? 4;
                        }}
                        cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        formatter={(value: string) => {
                          const labels: Record<string, string> = {
                            p97: 'Upper end of healthy range',
                            p50: 'Average for age',
                            p3: 'Lower end of healthy range',
                            childValue: 'Your Child',
                          };
                          return labels[value] || value;
                        }}
                      />
                      {/* WHO healthy range band: p3 to p97 shaded green */}
                      <Area type="monotone" dataKey="p97" stroke="#86efac" strokeWidth={1.5} strokeDasharray="4 4" fill="url(#whoBandGradient)" dot={false} activeDot={false} name="p97" />
                      <Area type="monotone" dataKey="p3" stroke="#86efac" strokeWidth={1.5} strokeDasharray="4 4" fill="transparent" dot={false} activeDot={false} name="p3" />
                      {/* WHO 50th percentile - IDEAL line, bold green */}
                      <Line type="monotone" dataKey="p50" stroke="#10b981" strokeWidth={2.5} strokeDasharray="8 4" dot={false} activeDot={false} name="p50" />
                      {/* Child's actual data */}
                      <Line
                        type="monotone"
                        dataKey="childValue"
                        stroke={activeGrowthChart === 'height' ? '#6366f1' : activeGrowthChart === 'weight' ? '#059669' : '#f59e0b'}
                        strokeWidth={3}
                        name="childValue"
                        dot={(props: any) => {
                          const { cx, cy, value, payload } = props;
                          if (value === null || value === undefined || value === 0) return <g key={`dot-empty-${cx}`} />;
                          const baseColor = activeGrowthChart === 'height' ? '#6366f1' : activeGrowthChart === 'weight' ? '#059669' : '#f59e0b';
                          const unit = activeGrowthChart === 'weight' ? 'kg' : 'cm';
                          // Color dot by status
                          let dotColor = baseColor;
                          if (payload?.p3 && payload?.p97) {
                            if (value < payload.p3) dotColor = '#dc2626';
                            else if (value > payload.p97) dotColor = '#7c3aed';
                          }
                          return (
                            <g key={`dot-${cx}-${cy}`}>
                              <circle cx={cx} cy={cy} r={7} fill={dotColor} stroke="#fff" strokeWidth={2.5} />
                              <text x={cx} y={cy - 14} textAnchor="middle" fontSize={11} fontWeight={700} fill={dotColor}>
                                {value}{unit}
                              </text>
                            </g>
                          );
                        }}
                        activeDot={(props: any) => {
                          const { cx, cy, value, payload } = props;
                          if (value === null || value === undefined || value === 0) return <g key={`adot-empty-${cx}`} />;
                          const unit = activeGrowthChart === 'weight' ? 'kg' : 'cm';
                          let dotColor = activeGrowthChart === 'height' ? '#6366f1' : activeGrowthChart === 'weight' ? '#059669' : '#f59e0b';
                          let statusText = '';
                          if (payload?.p3 && payload?.p50 && payload?.p97) {
                            if (value < payload.p3) { dotColor = '#dc2626'; statusText = 'Below Normal'; }
                            else if (value < payload.p50) { dotColor = '#d97706'; statusText = 'Below Ideal'; }
                            else if (value <= payload.p97) { dotColor = '#059669'; statusText = 'Healthy'; }
                            else { dotColor = '#7c3aed'; statusText = 'Above Normal'; }
                          }
                          const label = `${value}${unit}${statusText ? ` · ${statusText}` : ''}`;
                          const rectW = Math.max(60, label.length * 7 + 16);
                          return (
                            <g key={`adot-${cx}-${cy}`}>
                              <circle cx={cx} cy={cy} r={9} fill={dotColor} stroke="#fff" strokeWidth={3} />
                              <rect x={cx - rectW / 2} y={cy - 34} width={rectW} height={22} rx={6} fill={dotColor} />
                              <text x={cx} y={cy - 20} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff">
                                {label}
                              </text>
                            </g>
                          );
                        }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="cd-growth-reference-note">
                  Reference: WHO Child Growth Standards (0–24 months) &bull; {childGender === 'girl' ? 'Girls' : 'Boys'} &bull; Green band = Healthy range (3rd–97th percentile) &bull; Green dashed line = Ideal (50th percentile)
                </p>

              </div>

              {/* Growth Milestones */}
              <div className="cd-growth-milestones-section">
                <h3 className="cd-growth-milestones-title">
                  <FontAwesomeIcon icon={faStar} className="cd-icon-growth" />
                  {t('childDev:growth.growthMilestones')}
                </h3>
                <div className="cd-growth-milestone-progress">
                  <div className="cd-growth-milestone-progress-header">
                    <span>{growthMilestonesCompleted} of {growthMilestones.length} milestones completed</span>
                    <span className="cd-growth-milestone-percent">{growthMilestonePercent}%</span>
                  </div>
                  <div className="cd-growth-milestone-progress-bar" role="progressbar" aria-valuenow={growthMilestonePercent} aria-valuemin={0} aria-valuemax={100}>
                    <div className="cd-growth-milestone-progress-fill" style={{ width: `${growthMilestonePercent}%` }} />
                  </div>
                </div>
                <div className="cd-growth-milestones-grid">
                  {(['height', 'weight', 'head'] as const).map((cat) => (
                    <div key={cat} className="cd-growth-milestone-group">
                      <h4 className={`cd-growth-milestone-group-title cd-gm-${cat}`}>
                        <FontAwesomeIcon icon={cat === 'height' ? faRulerVertical : cat === 'weight' ? faWeight : faCircle} />
                        {cat === 'height' ? 'Height' : cat === 'weight' ? 'Weight' : 'Head Circumference'}
                      </h4>
                      {growthMilestones.filter((m) => m.category === cat).map((ms) => {
                        const record = growthMilestoneRecords[ms.id];
                        const isCompleted = record?.completed;
                        return (
                          <div key={ms.id} className={`cd-growth-milestone-item ${isCompleted ? 'completed' : ''}`}>
                            <label className="cd-growth-milestone-toggle">
                              <input
                                type="checkbox"
                                checked={!!isCompleted}
                                onChange={() => {
                                  if (isCompleted) {
                                    const updated = { ...growthMilestoneRecords, [ms.id]: { completed: false, date: '', value: '' } };
                                    saveGrowthMilestones(updated, ms.id);
                                  } else {
                                    const today = new Date().toISOString().split('T')[0];
                                    saveGrowthMilestones({
                                      ...growthMilestoneRecords,
                                      [ms.id]: { completed: true, date: today, value: '' },
                                    }, ms.id);
                                  }
                                }}
                              />
                              <span className="cd-growth-milestone-check">
                                {isCompleted && <FontAwesomeIcon icon={faCheck} />}
                              </span>
                              <div className="cd-growth-milestone-text">
                                <span className={`cd-growth-milestone-label ${isCompleted ? 'done' : ''}`}>{ms.label}</span>
                                <span className="cd-growth-milestone-age">{ms.ageRange}</span>
                              </div>
                            </label>
                            {isCompleted && record.date && (
                              <span className="cd-growth-milestone-date">
                                {new Date(record.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Growth Entry List */}
              {growthEntries.length === 0 ? (
                <div className="cd-empty-state">
                  <div className="cd-empty-icon-wrap">
                    <FontAwesomeIcon icon={faChartLine} className="cd-empty-icon" />
                  </div>
                  <h3>{t('childDev:growth.noEntries')}</h3>
                  <p>{t('childDev:growth.noEntriesDesc')}</p>
                </div>
              ) : (
                <div className="cd-growth-entries-section">
                  <h3 className="cd-growth-entries-title">
                    <FontAwesomeIcon icon={faCalendarAlt} className="cd-icon-growth" />
                    All Entries ({growthEntries.length})
                  </h3>
                  {/* Table View with Pagination */}
                  {(() => {
                    const sorted = [...growthEntries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    const totalPages = Math.ceil(sorted.length / growthTablePageSize);
                    const page = Math.min(growthTablePage, totalPages - 1);
                    const paged = sorted.slice(page * growthTablePageSize, (page + 1) * growthTablePageSize);
                    const hasWho = !!childDob;
                    return (
                      <>
                        {/* Legend for table status */}
                        <div className="cd-growth-table-legend">
                          <span style={{ color: '#059669', background: '#ecfdf5' }}>✓ Healthy (within WHO range)</span>
                          <span style={{ color: '#dc2626', background: '#fef2f2' }}>Below typical range</span>
                          <span style={{ color: '#7c3aed', background: '#f5f3ff' }}>Above typical range</span>
                          {!hasWho && <span style={{ color: '#94a3b8', fontStyle: 'italic', background: 'transparent' }}>Enter DOB to see WHO status</span>}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="cd-growth-table">
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left' }}>Date / Age</th>
                                <th className="col-height" style={{ textAlign: 'center' }}>Height (cm)<br /><span className="cd-th-sub">Ideal · Range</span></th>
                                <th className="col-weight" style={{ textAlign: 'center' }}>Weight (kg)<br /><span className="cd-th-sub">Ideal · Range</span></th>
                                <th className="col-head" style={{ textAlign: 'center' }}>Head (cm)<br /><span className="cd-th-sub">Ideal · Range</span></th>
                                <th style={{ textAlign: 'center' }}>Status</th>
                                <th style={{ textAlign: 'left' }}>Notes</th>
                                <th style={{ textAlign: 'center' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paged.map((entry) => {
                                const d = new Date(entry.date);
                                // Calculate age in months at this entry
                                const ageM = childDob
                                  ? Math.round((new Date(entry.date).getTime() - new Date(childDob).getTime()) / (30.44 * 24 * 60 * 60 * 1000))
                                  : null;
                                const ref = childGender === 'girl' ? whoReferenceData.girls : whoReferenceData.boys;
                                const whoH = (ageM !== null && ageM >= 0 && ageM <= 24) ? interpolateWho(ref.height, ageM) : null;
                                const whoW = (ageM !== null && ageM >= 0 && ageM <= 24) ? interpolateWho(ref.weight, ageM) : null;
                                const whoHd = (ageM !== null && ageM >= 0 && ageM <= 24) ? interpolateWho(ref.head, ageM) : null;
                                const hVal = entry.heightCm !== '' ? Number(entry.heightCm) : null;
                                const wVal = entry.weightKg !== '' ? Number(entry.weightKg) : null;
                                const hdVal = entry.headCircCm !== '' ? Number(entry.headCircCm) : null;
                                const getStatus = (val: number | null, who: typeof whoH) => {
                                  if (val === null || !who) return null;
                                  const pct = estimatePercentile(val, who);
                                  if (val < who.p3) return { label: 'Below typical', color: '#dc2626', bg: '#fef2f2', percentile: pct, severity: 'attention' as const };
                                  if (val > who.p97) return { label: 'Above typical', color: '#7c3aed', bg: '#f5f3ff', percentile: pct, severity: 'attention' as const };
                                  return { label: '✓ Healthy', color: '#059669', bg: '#ecfdf5', percentile: pct, severity: 'ok' as const };
                                };
                                const hStatus = getStatus(hVal, whoH);
                                const wStatus = getStatus(wVal, whoW);
                                const hdStatus = getStatus(hdVal, whoHd);
                                return (
                                  <tr key={entry.id}>
                                    <td className="td-date">
                                      {d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      <span className="date-sub">{d.toLocaleDateString('en-IN', { weekday: 'long' })}</span>
                                      {ageM !== null && ageM >= 0 && (
                                        <span className="cd-growth-age-pill">{ageM} mo</span>
                                      )}
                                    </td>
                                    <td className="td-metric">
                                      {hVal !== null ? (
                                        <div className="cd-metric-cell">
                                          <span className="metric-badge height-badge">
                                            <FontAwesomeIcon icon={faRulerVertical} style={{ fontSize: '0.7rem' }} />
                                            {hVal} cm
                                          </span>
                                          {whoH && (
                                            <span className="cd-who-hint-v2">
                                              WHO: {whoH.p3}–{whoH.p97} cm
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="metric-badge empty-badge">—</span>
                                      )}
                                    </td>
                                    <td className="td-metric">
                                      {wVal !== null ? (
                                        <div className="cd-metric-cell">
                                          <span className="metric-badge weight-badge">
                                            <FontAwesomeIcon icon={faWeight} style={{ fontSize: '0.7rem' }} />
                                            {wVal} kg
                                          </span>
                                          {whoW && (
                                            <span className="cd-who-hint-v2">
                                              WHO: {whoW.p3}–{whoW.p97} kg
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="metric-badge empty-badge">—</span>
                                      )}
                                    </td>
                                    <td className="td-metric">
                                      {hdVal !== null ? (
                                        <div className="cd-metric-cell">
                                          <span className="metric-badge head-badge">
                                            <FontAwesomeIcon icon={faCircle} style={{ fontSize: '0.5rem' }} />
                                            {hdVal} cm
                                          </span>
                                          {whoHd && (
                                            <span className="cd-who-hint-v2">
                                              WHO: {whoHd.p3}–{whoHd.p97} cm
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="metric-badge empty-badge">—</span>
                                      )}
                                    </td>
                                    <td className="td-status">
                                      <div className="cd-table-multi-status">
                                        {hStatus && (
                                          <span className="cd-mini-status" style={{ borderLeft: `3px solid ${hStatus.color}`, color: hStatus.color, background: hStatus.bg }}>
                                            H: {hStatus.label}{hStatus.severity === 'ok' ? ` · ~${hStatus.percentile}th` : ''}
                                          </span>
                                        )}
                                        {wStatus && (
                                          <span className="cd-mini-status" style={{ borderLeft: `3px solid ${wStatus.color}`, color: wStatus.color, background: wStatus.bg }}>
                                            W: {wStatus.label}{wStatus.severity === 'ok' ? ` · ~${wStatus.percentile}th` : ''}
                                          </span>
                                        )}
                                        {hdStatus && (
                                          <span className="cd-mini-status" style={{ borderLeft: `3px solid ${hdStatus.color}`, color: hdStatus.color, background: hdStatus.bg }}>
                                            Hd: {hdStatus.label}{hdStatus.severity === 'ok' ? ` · ~${hdStatus.percentile}th` : ''}
                                          </span>
                                        )}
                                        {!hStatus && !wStatus && !hdStatus && (
                                          <span className="cd-healthy-badge">No WHO data</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="td-notes">
                                      {entry.notes ? (
                                        <span className="notes-text">{entry.notes}</span>
                                      ) : (
                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                      )}
                                    </td>
                                    <td className="td-actions">
                                      <div className="actions-wrap">
                                        <button className="action-btn cd-edit-btn" onClick={() => handleEditGrowth(entry)} title="Edit">
                                          <FontAwesomeIcon icon={faPencilAlt} />
                                        </button>
                                        <button className="action-btn cd-delete-btn" onClick={() => askDelete('Delete growth entry?', 'This growth entry will be permanently removed.', () => deleteGrowthEntry(entry.id))} title="Delete">
                                          <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {totalPages > 1 && (
                          <div className="cd-growth-pagination">
                            <span className="page-info">Showing {page * growthTablePageSize + 1}–{Math.min((page + 1) * growthTablePageSize, sorted.length)} of {sorted.length} entries</span>
                            <div className="page-controls">
                              <button className="page-btn" onClick={() => setGrowthTablePage(0)} disabled={page === 0}>First</button>
                              <button className="page-btn" onClick={() => setGrowthTablePage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
                              <span className="page-indicator">{page + 1} / {totalPages}</span>
                              <button className="page-btn" onClick={() => setGrowthTablePage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</button>
                              <button className="page-btn" onClick={() => setGrowthTablePage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ══════════ MILESTONES TAB (Enhanced) ══════════ */}
          {activeTab === 'Milestones' && (
            <div className="cd-milestones-tab">
              <div className="cd-tab-header cd-tab-header-milestones">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faStar} className="cd-tab-header-icon cd-icon-milestones" />
                    {t('childDev:milestones.title')}
                  </h2>
                  <p>{t('childDev:milestones.description')}</p>
                </div>
                <div className="cd-tab-header-img">
                  <img src="/healty1.png" alt="Milestones" />
                </div>
              </div>

              {/* Month Selector */}
              <div className="cd-ms-month-selector">
                <span className="cd-ms-month-label">{t('childDev:milestones.selectMonth')}</span>
                <div className="cd-ms-month-pills">
                  {milestoneMonthKeys.map((mk) => {
                    const isCurrentMonth = childAgeInMonths !== null && String(mk) === String(
                      (() => {
                        const monthNums = milestoneMonthKeys.map(Number).sort((a, b) => a - b);
                        let best = monthNums[0];
                        for (const m of monthNums) { if (m <= childAgeInMonths + 1) best = m; else break; }
                        return best;
                      })()
                    );
                    return (
                      <button
                        key={mk}
                        className={`cd-ms-month-pill ${selectedMilestoneMonth === mk ? 'active' : ''} ${isCurrentMonth ? 'current-month' : ''}`}
                        onClick={() => setSelectedMilestoneMonth(mk)}
                      >
                        {mk}m
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Milestone Month Header + Info Bar */}
              <div className="cd-ms-info-bar">
                <h3 className="cd-ms-month-heading">
                  <FontAwesomeIcon icon={faStar} className="cd-ms-heading-icon" />
                  Milestones for {milestoneMonthLabel} Month
                </h3>
                <div className="cd-ms-info-dates">
                  {milestoneLastUpdated && (
                    <span className="cd-ms-last-updated">
                      <FontAwesomeIcon icon={faCalendarAlt} /> Last updated: {new Date(milestoneLastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {milestoneDueDate && (
                    <span className={`cd-ms-due-date ${milestoneDueDate < new Date() ? 'overdue' : ''}`}>
                      <FontAwesomeIcon icon={faClock} /> Due on: {milestoneDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Summary Rings */}
              <div className="cd-ms-progress-summary">
                {Object.entries(milestoneProgressByCategory).map(([cat, data]) => {
                  const pct = data.total > 0 ? Math.round((data.yes / data.total) * 100) : 0;
                  return (
                    <div key={cat} className="cd-ms-progress-ring-card">
                      <div className="cd-ms-ring-wrap">
                        <svg viewBox="0 0 36 36" className="cd-ms-ring-svg">
                          <path className="cd-ms-ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                          <path className="cd-ms-ring-fill" strokeDasharray={`${pct}, 100`} style={{ stroke: data.color }} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                        </svg>
                        <span className="cd-ms-ring-pct">{pct}%</span>
                      </div>
                      <span className="cd-ms-ring-label" style={{ color: data.color }}>{cat}</span>
                      <span className="cd-ms-ring-count">{data.yes}/{data.total}</span>
                    </div>
                  );
                })}
              </div>

              {/* Category Accordions */}
              <div className="cd-ms-categories">
                {currentMonthMilestones.map((catData) => {
                  const isExpanded = expandedMilestoneCategory === catData.category;
                  const catProgress = milestoneProgressByCategory[catData.category];
                  const completedCount = catProgress ? catProgress.yes : 0;
                  return (
                    <div key={catData.category} className="cd-ms-category-card" style={{ '--ms-cat-color': catData.color } as React.CSSProperties}>
                      <div
                        className="cd-ms-category-header"
                        onClick={() => setExpandedMilestoneCategory(isExpanded ? null : catData.category)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedMilestoneCategory(isExpanded ? null : catData.category); }}}
                      >
                        <div className="cd-ms-category-header-left">
                          <span className="cd-ms-category-dot" style={{ background: catData.color }} />
                          <div>
                            <h3 className="cd-ms-category-name">{catData.category}</h3>
                            <span className="cd-ms-category-count">{completedCount}/{catData.items.length} {t('childDev:milestones.achieved')}</span>
                          </div>
                        </div>
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="cd-ms-category-toggle" />
                      </div>
                      <div className="cd-ms-category-progress-bar">
                        <div className="cd-ms-category-progress-fill" style={{ width: `${catData.items.length > 0 ? (completedCount / catData.items.length) * 100 : 0}%`, background: catData.color }} />
                      </div>
                      {isExpanded && (
                        <div className="cd-ms-question-list">
                          {catData.items.map((item) => {
                            const assessment = milestoneAssessments[item.id];
                            const currentStatus = assessment?.status || null;
                            return (
                              <div key={item.id} className="cd-ms-question-item">
                                <span className="cd-ms-question-text">{item.text}</span>
                                <div className="cd-ms-toggle-group">
                                  <button
                                    className={`cd-ms-toggle-btn cd-ms-btn-yes ${currentStatus === 'yes' ? 'active' : ''}`}
                                    onClick={() => {
                                      const today = new Date().toISOString().split('T')[0];
                                      saveMilestoneAssessments({ ...milestoneAssessments, [item.id]: { status: currentStatus === 'yes' ? null : 'yes', date: today } }, item.id);
                                    }}
                                  >{t('childDev:yes')}</button>
                                  <button
                                    className={`cd-ms-toggle-btn cd-ms-btn-almost ${currentStatus === 'almost' ? 'active' : ''}`}
                                    onClick={() => {
                                      const today = new Date().toISOString().split('T')[0];
                                      saveMilestoneAssessments({ ...milestoneAssessments, [item.id]: { status: currentStatus === 'almost' ? null : 'almost', date: today } }, item.id);
                                    }}
                                  >{t('childDev:almost')}</button>
                                  <button
                                    className={`cd-ms-toggle-btn cd-ms-btn-no ${currentStatus === 'no' ? 'active' : ''}`}
                                    onClick={() => {
                                      const today = new Date().toISOString().split('T')[0];
                                      saveMilestoneAssessments({ ...milestoneAssessments, [item.id]: { status: currentStatus === 'no' ? null : 'no', date: today } }, item.id);
                                    }}
                                  >{t('childDev:no')}</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Compare Milestones Button */}
              <button className="cd-ms-compare-btn" onClick={() => setShowMilestoneComparison(true)}>
                <FontAwesomeIcon icon={faChartLine} /> {t('childDev:milestones.compareMilestones')}
              </button>

              {/* ── Milestone Comparison Dialog ── */}
              {showMilestoneComparison && (
              <div className="cd-ms-compare-overlay" onClick={() => setShowMilestoneComparison(false)}>
                <div className="cd-ms-compare-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="cd-ms-compare-header">
                    <h2>
                      <FontAwesomeIcon icon={faChartLine} className="cd-ms-compare-title-icon" />
                      {t('childDev:milestones.milestoneComparison')}
                    </h2>
                    <button className="cd-ms-compare-close" onClick={() => setShowMilestoneComparison(false)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <p className="cd-ms-compare-subtitle">{t('childDev:milestones.comparisonSubtitle')}</p>

                  {/* Stacked Bar Chart */}
                  <div className="cd-ms-compare-chart">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={milestoneComparisonData.chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                        />
                        <Legend />
                        <Bar dataKey="Yes" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="Almost" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="Not Done" stackId="a" fill="#e2e8f0" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Category Breakdown Table */}
                  <div className="cd-ms-compare-table-wrap">
                    <table className="cd-ms-compare-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          {milestoneComparisonData.categories.map((cat) => (
                            <th key={cat}>
                              <span className="cd-ms-compare-th-dot" style={{ background: milestoneComparisonData.categoryColors[cat] }} />
                              {cat}
                            </th>
                          ))}
                          <th>Overall</th>
                        </tr>
                      </thead>
                      <tbody>
                        {milestoneComparisonData.tableRows.map((row) => {
                          const overallPct = row.overallTotal > 0 ? Math.round((row.overallYes / row.overallTotal) * 100) : 0;
                          return (
                            <tr key={row.month}>
                              <td className="cd-ms-compare-month-cell">{row.month}m</td>
                              {milestoneComparisonData.categories.map((cat) => {
                                const s = row.catStats[cat];
                                const pct = s.total > 0 ? Math.round((s.yes / s.total) * 100) : 0;
                                return (
                                  <td key={cat}>
                                    <div className="cd-ms-compare-cell">
                                      <span className="cd-ms-compare-cell-count">{s.yes}/{s.total}</span>
                                      <span
                                        className={`cd-ms-compare-cell-pill ${pct === 100 ? 'complete' : pct > 0 ? 'partial' : 'empty'}`}
                                      >
                                        {pct}%
                                      </span>
                                    </div>
                                  </td>
                                );
                              })}
                              <td>
                                <div className="cd-ms-compare-cell">
                                  <span className="cd-ms-compare-cell-count">{row.overallYes}/{row.overallTotal}</span>
                                  <span
                                    className={`cd-ms-compare-cell-pill ${overallPct === 100 ? 'complete' : overallPct > 0 ? 'partial' : 'empty'}`}
                                  >
                                    {overallPct}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="cd-ms-compare-month-cell"><strong>Total</strong></td>
                          {milestoneComparisonData.categories.map((cat) => {
                            const totalYes = milestoneComparisonData.tableRows.reduce((s, r) => s + r.catStats[cat].yes, 0);
                            const totalAll = milestoneComparisonData.tableRows.reduce((s, r) => s + r.catStats[cat].total, 0);
                            return (
                              <td key={cat}>
                                <div className="cd-ms-compare-cell">
                                  <span className="cd-ms-compare-cell-count"><strong>{totalYes}/{totalAll}</strong></span>
                                </div>
                              </td>
                            );
                          })}
                          {(() => {
                            const grandYes = milestoneComparisonData.tableRows.reduce((s, r) => s + r.overallYes, 0);
                            const grandTotal = milestoneComparisonData.tableRows.reduce((s, r) => s + r.overallTotal, 0);
                            const grandPct = grandTotal > 0 ? Math.round((grandYes / grandTotal) * 100) : 0;
                            return (
                              <td>
                                <div className="cd-ms-compare-cell">
                                  <span className="cd-ms-compare-cell-count"><strong>{grandYes}/{grandTotal}</strong></span>
                                  <span className={`cd-ms-compare-cell-pill ${grandPct === 100 ? 'complete' : grandPct > 0 ? 'partial' : 'empty'}`}>
                                    {grandPct}%
                                  </span>
                                </div>
                              </td>
                            );
                          })()}
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <button className="cd-ms-compare-close-btn" onClick={() => setShowMilestoneComparison(false)}>
                    {t('childDev:close')}
                  </button>
                </div>
              </div>
            )}
            </div>
          )}

          {/* ══════════ MEMORIES TAB (Enhanced with Moments) ══════════ */}
          {activeTab === 'Memories' && (
            <div className="cd-memories-tab">
              <div className="cd-tab-header cd-tab-header-memories">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faCamera} className="cd-tab-header-icon cd-icon-memories" />
                    {t('childDev:memories.title')}
                  </h2>
                  <p>{t('childDev:memories.description')}</p>
                </div>
              </div>

              {/* Moments Section */}
              <div className="cd-moments-section">
                <h3 className="cd-moments-title">
                  <FontAwesomeIcon icon={faBaby} className="cd-icon-memories" /> {t('childDev:memories.babyMoments')}
                </h3>
                <p className="cd-moments-desc">{t('childDev:memories.momentsDesc')}</p>
                <div className="cd-moments-grid">
                  {momentPresets.map((mp) => {
                    const rec = moments[mp.id];
                    const isCompleted = rec?.completed;
                    return (
                      <div
                        key={mp.id}
                        className={`cd-moment-card ${isCompleted ? 'completed' : ''}`}
                        onClick={() => {
                          if (isCompleted) {
                            const updated = { ...moments, [mp.id]: { completed: false, date: '' } };
                            saveMoments(updated, mp.id);
                          } else {
                            const today = new Date().toISOString().split('T')[0];
                            saveMoments({ ...moments, [mp.id]: { completed: true, date: today } }, mp.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`${mp.label} - ${isCompleted ? 'completed' : 'not completed'}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (isCompleted) {
                              const updated = { ...moments, [mp.id]: { completed: false, date: '' } };
                              saveMoments(updated, mp.id);
                            } else {
                              const today = new Date().toISOString().split('T')[0];
                              saveMoments({ ...moments, [mp.id]: { completed: true, date: today } }, mp.id);
                            }
                          }
                        }}
                      >
                        <div className="cd-moment-circle">
                          {isCompleted ? (
                            <FontAwesomeIcon icon={faCheck} className="cd-moment-check-icon" />
                          ) : (
                            <FontAwesomeIcon icon={faPlus} className="cd-moment-plus-icon" />
                          )}
                        </div>
                        <span className="cd-moment-label">{mp.label}</span>
                        {isCompleted && rec.date && (
                          <span className="cd-moment-date">
                            {new Date(rec.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="cd-memories-header">
                <h2 className="cd-section-title">
                  <FontAwesomeIcon icon={faCamera} className="cd-title-icon cd-icon-memories" />
                  {t('childDev:memories.memoryJournal')}
                </h2>
                <button
                  className="cd-add-btn"
                  onClick={() => setShowMemoryForm(!showMemoryForm)}
                  aria-label="Add new memory"
                >
                  <FontAwesomeIcon icon={faPlus} /> {t('childDev:memories.addMemory')}
                </button>
              </div>

              {showMemoryForm && (
                <div className="cd-memory-form-card">
                  <h3>{t('childDev:memories.newMemory')}</h3>
                  <div className="cd-memory-form-grid">
                    <div className="cd-form-group">
                      <label htmlFor="memory-title">{t('childDev:memories.memoryTitle')}</label>
                      <input
                        id="memory-title"
                        type="text"
                        placeholder="e.g. First day at park"
                        value={memoryForm.title}
                        onChange={(e) => setMemoryForm({ ...memoryForm, title: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="memory-date">{t('childDev:memories.memoryDate')}</label>
                      <input
                        id="memory-date"
                        type="date"
                        value={memoryForm.date}
                        onChange={(e) => setMemoryForm({ ...memoryForm, date: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group cd-form-full-width">
                      <label htmlFor="memory-desc">{t('childDev:memories.memoryDescription')}</label>
                      <textarea
                        id="memory-desc"
                        placeholder="Describe this memory..."
                        rows={3}
                        value={memoryForm.description}
                        onChange={(e) => setMemoryForm({ ...memoryForm, description: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group cd-form-full-width">
                      <label htmlFor="memory-photo">Photo</label>
                      <input
                        id="memory-photo"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setMemoryPhoto(e.target.files?.[0] || null)}
                      />
                      {memoryPhoto && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <img
                            src={URL.createObjectURL(memoryPhoto)}
                            alt="Selected preview"
                            style={{ maxWidth: 140, maxHeight: 140, borderRadius: 8, objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setMemoryPhoto(null);
                              const input = document.getElementById('memory-photo') as HTMLInputElement | null;
                              if (input) input.value = '';
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '6px 12px',
                              background: '#fef2f2',
                              color: '#b91c1c',
                              border: '1px solid #fecaca',
                              borderRadius: 6,
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="cd-memory-form-actions">
                    <button
                      className="cd-btn-primary"
                      onClick={() => {
                        if (!memoryForm.title) return;
                        const newMemory: MemoryEntry = {
                          id: Date.now().toString(),
                          title: memoryForm.title,
                          date: memoryForm.date || new Date().toISOString().split('T')[0],
                          description: memoryForm.description,
                          photo: memoryPhoto ? URL.createObjectURL(memoryPhoto) : null,
                        };
                        const updated = [newMemory, ...memories];
                        saveMemories(updated, newMemory, memoryPhoto);
                        setMemoryForm({ title: '', date: '', description: '' });
                        setMemoryPhoto(null);
                        setShowMemoryForm(false);
                      }}
                    >
                      {t('childDev:memories.saveMemory')}
                    </button>
                    <button
                      className="cd-btn-secondary"
                      onClick={() => {
                        setShowMemoryForm(false);
                        setMemoryForm({ title: '', date: '', description: '' });
                        setMemoryPhoto(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {memories.length === 0 ? (
                <div className="cd-empty-state">
                  <div className="cd-empty-icon-wrap">
                    <FontAwesomeIcon icon={faCamera} className="cd-empty-icon" />
                  </div>
                  <h3>{t('childDev:memories.noMemories')}</h3>
                  <p>{t('childDev:memories.noMemoriesDesc')}</p>
                </div>
              ) : (
                <div className="cd-memories-grid">
                  {memories.map((mem, idx) => (
                    <div key={mem.id} className="cd-memory-card" style={{ background: pastelColors[idx % pastelColors.length] }}>
                      <div className="cd-memory-card-header">
                        <h3>{mem.title}</h3>
                        <button
                          className="cd-delete-btn"
                          aria-label={`Delete memory: ${mem.title}`}
                          onClick={() => askDelete('Delete memory?', `"${mem.title}" will be permanently removed.`, () => deleteMemoryEntry(mem.id))}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      <div className="cd-memory-date">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {new Date(mem.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      {mem.photo && (
                        <img
                          src={mem.photo}
                          alt={mem.title}
                          className="cd-memory-photo"
                          style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, margin: '8px 0' }}
                        />
                      )}
                      {mem.description && (
                        <p className="cd-memory-desc">{mem.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════ TEETHING TAB ══════════ */}
          {activeTab === 'Teething' && (
            <div className="cd-teething-tab">
              <div className="cd-tab-header cd-tab-header-teething">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faTooth} className="cd-tab-header-icon cd-icon-teething" />
                    {t('childDev:teething.title')}
                  </h2>
                  <p>{t('childDev:teething.description')}</p>
                </div>
              </div>

              <div className="cd-teething-summary">
                <div className="cd-teething-summary-circle">
                  <span className="cd-teething-count">{teethAppeared}</span>
                  <span className="cd-teething-total">/{teethData.length}</span>
                </div>
                <span className="cd-teething-label">{t('childDev:teething.teethAppeared')}</span>
              </div>

              {/* Interactive Dental Arch SVG */}
              <div className="cd-dental-arch-container">
                <p className="cd-dental-arch-hint">{t('childDev:teething.tapToLog')}</p>
                <svg viewBox="0 0 300 320" className="cd-dental-arch-svg">
                  {/* Upper jaw arc background */}
                  <ellipse cx="150" cy="130" rx="120" ry="100" fill="#ede7f6" stroke="#d1c4e9" strokeWidth="2" />
                  {/* Lower jaw arc background */}
                  <ellipse cx="150" cy="190" rx="100" ry="80" fill="#e8eaf6" stroke="#c5cae9" strokeWidth="2" />
                  {/* Tongue area */}
                  <ellipse cx="150" cy="185" rx="50" ry="35" fill="#f8bbd0" opacity="0.5" />

                  {/* Upper teeth (10) - arranged in an arc */}
                  {teethData.filter(t => t.jaw === 'upper').map(tooth => {
                    const rad = (tooth.angle * Math.PI) / 180;
                    const cx = 150 + Math.sin(rad) * 95;
                    const cy = 130 - Math.cos(rad) * 80;
                    const appeared = teethRecords[tooth.id]?.appeared;
                    const isMolar = tooth.toothType.includes('Molar');
                    const r = isMolar ? 14 : tooth.toothType === 'Canine' ? 11 : 10;
                    return (
                      <g key={tooth.id} onClick={() => {
                        if (!appeared) {
                          const today = new Date().toISOString().split('T')[0];
                          saveTeeth({ ...teethRecords, [tooth.id]: { appeared: true, date: today, comment: '' } }, tooth.id);
                        }
                      }} style={{ cursor: 'pointer' }}>
                        <circle cx={cx} cy={cy} r={r + 2} fill={appeared ? '#7c4dff' : '#e0e0e0'} opacity="0.3" />
                        <circle cx={cx} cy={cy} r={r} fill={appeared ? '#7c4dff' : '#fff'} stroke={appeared ? '#651fff' : '#bdbdbd'} strokeWidth="1.5" />
                        {appeared && <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="8" fontWeight="bold">&#10003;</text>}
                      </g>
                    );
                  })}

                  {/* Lower teeth (10) - arranged in an arc */}
                  {teethData.filter(t => t.jaw === 'lower').map(tooth => {
                    const rad = (tooth.angle * Math.PI) / 180;
                    const cx = 150 + Math.sin(rad) * 80;
                    const cy = 190 + Math.cos(rad) * 60;
                    const appeared = teethRecords[tooth.id]?.appeared;
                    const isMolar = tooth.toothType.includes('Molar');
                    const r = isMolar ? 14 : tooth.toothType === 'Canine' ? 11 : 10;
                    return (
                      <g key={tooth.id} onClick={() => {
                        if (!appeared) {
                          const today = new Date().toISOString().split('T')[0];
                          saveTeeth({ ...teethRecords, [tooth.id]: { appeared: true, date: today, comment: '' } }, tooth.id);
                        }
                      }} style={{ cursor: 'pointer' }}>
                        <circle cx={cx} cy={cy} r={r + 2} fill={appeared ? '#7c4dff' : '#e0e0e0'} opacity="0.3" />
                        <circle cx={cx} cy={cy} r={r} fill={appeared ? '#7c4dff' : '#fff'} stroke={appeared ? '#651fff' : '#bdbdbd'} strokeWidth="1.5" />
                        {appeared && <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="8" fontWeight="bold">&#10003;</text>}
                      </g>
                    );
                  })}

                  {/* Labels */}
                  <text x="150" y="28" textAnchor="middle" fill="#5e35b1" fontSize="11" fontWeight="600">Upper Jaw</text>
                  <text x="150" y="308" textAnchor="middle" fill="#3949ab" fontSize="11" fontWeight="600">Lower Jaw</text>
                </svg>
              </div>

              <div className="cd-teething-list">
                {teethData.map((tooth) => {
                  const record = teethRecords[tooth.id];
                  const hasAppeared = record?.appeared;
                  return (
                    <div
                      key={tooth.id}
                      className={`cd-tooth-card ${hasAppeared ? 'appeared' : ''}`}
                    >
                      <div className="cd-tooth-header">
                        <div className="cd-tooth-info">
                          <FontAwesomeIcon icon={faTooth} className="cd-tooth-icon" />
                          <div>
                            <h4 className="cd-tooth-name">{tooth.toothType}</h4>
                            <span className="cd-tooth-age">{tooth.name} &middot; {tooth.typicalAge}</span>
                          </div>
                        </div>
                        {hasAppeared && (
                          <span className="cd-tooth-appeared-badge">
                            <FontAwesomeIcon icon={faCheck} /> {t('childDev:teething.appeared')}
                          </span>
                        )}
                      </div>
                      <div className="cd-tooth-actions">
                        {!hasAppeared ? (
                          <div className="cd-tooth-mark-row">
                            <input
                              type="date"
                              className="cd-tooth-date-input"
                              id={`tooth-date-${tooth.id}`}
                              aria-label={`Date for ${tooth.name}`}
                            />
                            <input
                              type="text"
                              placeholder="Optional comment"
                              className="cd-tooth-comment-input"
                              id={`tooth-comment-${tooth.id}`}
                              aria-label={`Comment for ${tooth.name}`}
                            />
                            <button
                              className="cd-btn-primary cd-btn-sm"
                              onClick={() => {
                                const dateEl = document.getElementById(`tooth-date-${tooth.id}`) as HTMLInputElement;
                                const commentEl = document.getElementById(`tooth-comment-${tooth.id}`) as HTMLInputElement;
                                const dateVal = dateEl?.value || new Date().toISOString().split('T')[0];
                                const commentVal = commentEl?.value || '';
                                const updated = {
                                  ...teethRecords,
                                  [tooth.id]: { appeared: true, date: dateVal, comment: commentVal },
                                };
                                saveTeeth(updated, tooth.id);
                              }}
                            >
                              {t('childDev:teething.markAppeared')}
                            </button>
                          </div>
                        ) : (
                          <div className="cd-tooth-detail">
                            <span>
                              <FontAwesomeIcon icon={faCalendarAlt} /> {new Date(record.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            {record.comment && <span className="cd-tooth-comment">{record.comment}</span>}
                            <button
                              className="cd-btn-outline cd-btn-sm"
                              onClick={() => {
                                const updated = { ...teethRecords, [tooth.id]: { appeared: false, date: '', comment: '' } };
                                saveTeeth(updated, tooth.id);
                              }}
                              aria-label={`Undo ${tooth.name} appearance`}
                            >
                              Undo
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════════ VACCINATION TAB ══════════ */}
          {activeTab === 'Vaccination' && (
            <div className="cd-vaccination-tab">
              <div className="cd-tab-header cd-tab-header-vaccination">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faShieldAlt} className="cd-tab-header-icon cd-icon-vaccination" />
                    {t('childDev:vaccinations.title')}
                  </h2>
                  <p>{t('childDev:vaccinations.description')}</p>
                </div>
                <div className="cd-vacc-dob-inline">
                  <label htmlFor="child-dob">{t('childDev:vaccinations.childDob')}</label>
                  <input
                    id="child-dob"
                    type="date"
                    value={childDob}
                    onChange={(e) => {
                      setChildDob(e.target.value);
                      updateChildProfile('dob', e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className="cd-vacc-progress-wrapper">
                <div className="cd-vacc-progress-header">
                  <span>{vaccCompleted} of {vaccinationSchedule.length} vaccinations completed</span>
                  <span className="cd-vacc-percent">{vaccPercent}%</span>
                </div>
                <div className="cd-vacc-progress-bar" role="progressbar" aria-valuenow={vaccPercent} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="cd-vacc-progress-fill"
                    style={{ width: `${vaccPercent}%` }}
                  />
                </div>
              </div>

              <div className="cd-vacc-timeline">
                {Object.entries(vaccinationGroups).map(([age, vaccines], groupIdx) => (
                  <div key={age} className="cd-vacc-age-group">
                    <div className="cd-vacc-timeline-marker">
                      <span className="cd-vacc-timeline-dot" />
                      {groupIdx < Object.keys(vaccinationGroups).length - 1 && (
                        <span className="cd-vacc-timeline-line" />
                      )}
                    </div>
                    <div className="cd-vacc-age-content">
                      <h3 className="cd-vacc-age-heading">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {age}
                      </h3>
                      <div className="cd-vacc-cards">
                        {vaccines.map((vacc) => {
                          const record = vaccinationRecords[vacc.id];
                          const isCompleted = record?.completed;
                          const rawStatus = getVaccinationStatus(vacc.age);
                          const status = isCompleted ? 'completed' : rawStatus;

                          return (
                            <div key={vacc.id} className={`cd-vacc-card cd-vacc-${status}`}>
                              <div className="cd-vacc-card-top">
                                <div className="cd-vacc-name-row">
                                  <FontAwesomeIcon icon={faSyringe} className="cd-vacc-icon" />
                                  <h4 className="cd-vacc-name">{vacc.name}</h4>
                                </div>
                                <span className={`cd-vacc-status-badge cd-status-${status}`}>
                                  {status === 'completed' && <><FontAwesomeIcon icon={faCheck} /> {t('childDev:vaccinations.completed')}</>}
                                  {status === 'due' && <><FontAwesomeIcon icon={faClock} /> {t('childDev:vaccinations.due')}</>}
                                  {status === 'overdue' && <><FontAwesomeIcon icon={faClock} /> {t('childDev:vaccinations.overdue')}</>}
                                </span>
                              </div>
                              <p className="cd-vacc-desc">{vacc.description}</p>
                              {isCompleted ? (
                                <div className="cd-vacc-completed-info">
                                  <FontAwesomeIcon icon={faCircleCheck} className="cd-vacc-completed-icon" />
                                  <span>{t('childDev:vaccinations.completedOn')} {new Date(record.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                  <button
                                    className="cd-btn-outline cd-btn-sm"
                                    onClick={() => {
                                      const updated = { ...vaccinationRecords, [vacc.id]: { completed: false, date: '' } };
                                      saveVaccinations(updated, vacc.id);
                                    }}
                                    aria-label={`Undo ${vacc.name} completion`}
                                  >
                                    Undo
                                  </button>
                                </div>
                              ) : (
                                <div className="cd-vacc-mark-row">
                                  <input
                                    type="date"
                                    className="cd-vacc-date-input"
                                    id={`vacc-date-${vacc.id}`}
                                    aria-label={`Date for ${vacc.name}`}
                                  />
                                  <button
                                    className="cd-btn-primary cd-btn-sm"
                                    onClick={() => {
                                      const dateEl = document.getElementById(`vacc-date-${vacc.id}`) as HTMLInputElement;
                                      const dateVal = dateEl?.value || new Date().toISOString().split('T')[0];
                                      const updated = {
                                        ...vaccinationRecords,
                                        [vacc.id]: { completed: true, date: dateVal },
                                      };
                                      saveVaccinations(updated, vacc.id);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} /> {t('childDev:vaccinations.markComplete')}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══════════ SLEEPING TAB ══════════ */}
          {activeTab === 'Sleeping' && (
            <div className="cd-sleeping-tab">
              <div className="cd-tab-header cd-tab-header-sleeping">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faMoon} className="cd-tab-header-icon cd-icon-sleeping" />
                    {t('childDev:sleeping.title')}
                  </h2>
                  <p>{t('childDev:sleeping.description')}</p>
                </div>
                <div className="cd-tab-header-img">
                  <img src="/sleep.png" alt="Sleep" />
                </div>
              </div>

              {/* Sleep Guide Reference */}
              <div className="cd-sleep-guide-card">
                <div className="cd-sleep-guide-header" onClick={() => setShowSleepGuide(!showSleepGuide)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSleepGuide(!showSleepGuide); } }}>
                  <div className="cd-sleep-guide-header-left">
                    <FontAwesomeIcon icon={faBed} className="cd-sleep-guide-icon" />
                    <span>{t('childDev:sleeping.recommendedSleep')}</span>
                  </div>
                  <FontAwesomeIcon icon={showSleepGuide ? faChevronUp : faChevronDown} className="cd-sleep-guide-toggle" />
                </div>
                {showSleepGuide && (
                  <div className="cd-sleep-guide-body">
                    <div className="cd-sleep-guide-table-wrap">
                      <table className="cd-sleep-guide-table">
                        <thead>
                          <tr>
                            <th>Age</th>
                            <th>Total Sleep</th>
                            <th>Night</th>
                            <th>Naps</th>
                            <th>Nap Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sleepGuideData.map((row) => {
                            const isCurrentAge = childAgeInMonths !== null && childAgeInMonths >= row.minMonth && childAgeInMonths < row.maxMonth;
                            return (
                              <tr key={row.ageRange} className={isCurrentAge ? 'cd-sleep-guide-active-row' : ''}>
                                <td className="cd-sleep-guide-age-cell">{row.ageRange}</td>
                                <td>{row.totalSleep}</td>
                                <td>{row.nightSleep}</td>
                                <td>{row.naps}</td>
                                <td>{row.napDuration}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="cd-sleep-guide-source">Based on AAP & NSF guidelines</p>
                  </div>
                )}
              </div>

              {/* Sleep Timer */}
              <div className="cd-sleep-timer-section">
                <p className="cd-sleep-timer-hint">
                  {sleepTimerRunning ? t('childDev:sleeping.babySleeping') : t('childDev:sleeping.startTimer')}
                </p>
                <div
                  className={`cd-sleep-timer-circle ${sleepTimerRunning ? 'running' : ''}`}
                  onClick={handleSleepTimerToggle}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSleepTimerToggle(); } }}
                  aria-label={sleepTimerRunning ? 'Stop sleep timer' : 'Start sleep timer'}
                >
                  <FontAwesomeIcon icon={faBaby} className="cd-sleep-timer-icon" />
                  <span className="cd-sleep-timer-label">{sleepTimerRunning ? t('childDev:sleeping.stop') : t('childDev:sleeping.start')}</span>
                </div>
                {sleepTimerRunning && (
                  <div className="cd-sleep-timer-elapsed">
                    <FontAwesomeIcon icon={faClock} /> {formatTimerElapsed(sleepTimerElapsed)}
                  </div>
                )}
              </div>

              <div className="cd-sleeping-header">
                <h2 className="cd-section-title">
                  <FontAwesomeIcon icon={faBed} className="cd-title-icon cd-icon-sleeping" />
                  {t('childDev:sleeping.sleepLog')}
                </h2>
                <button
                  className="cd-add-btn"
                  onClick={() => setShowSleepForm(!showSleepForm)}
                  aria-label="Add new sleep entry"
                >
                  <FontAwesomeIcon icon={faPlus} /> {t('childDev:sleeping.manualEntry')}
                </button>
              </div>

              {/* Sleep Calendar */}
              <div className="cd-sleep-calendar">
                <div className="cd-sleep-calendar-header">
                  <button className="cd-cal-nav-btn" onClick={prevMonth} aria-label="Previous month">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <span className="cd-cal-month-name">
                    {monthNames[sleepCalendarMonth.month]} {sleepCalendarMonth.year}
                  </span>
                  <button className="cd-cal-nav-btn" onClick={nextMonth} aria-label="Next month">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
                <div className="cd-cal-grid">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="cd-cal-day-header">
                      {day}
                    </div>
                  ))}
                  {Array.from({ length: getFirstDayOfMonth(sleepCalendarMonth.year, sleepCalendarMonth.month) }).map((_, i) => (
                    <div key={`empty-${i}`} className="cd-cal-day empty" />
                  ))}
                  {Array.from({ length: getDaysInMonth(sleepCalendarMonth.year, sleepCalendarMonth.month) }).map((_, i) => {
                    const dayNum = i + 1;
                    const dateStr = `${sleepCalendarMonth.year}-${String(sleepCalendarMonth.month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                    const hasSleep = sleepDatesSet.has(dateStr);
                    return (
                      <div
                        key={dayNum}
                        className={`cd-cal-day ${hasSleep ? 'has-data' : ''}`}
                      >
                        {dayNum}
                        {hasSleep && <span className="cd-cal-dot" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {showSleepForm && (
                <div className="cd-sleep-form-card">
                  <h3>{t('childDev:sleeping.newSleepEntry')}</h3>
                  <div className="cd-sleep-form-grid">
                    <div className="cd-form-group">
                      <label htmlFor="sleep-date">{t('childDev:sleeping.date')}</label>
                      <input
                        id="sleep-date"
                        type="date"
                        value={sleepForm.date}
                        onChange={(e) => setSleepForm({ ...sleepForm, date: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="sleep-start">{t('childDev:sleeping.startTime')}</label>
                      <input
                        id="sleep-start"
                        type="time"
                        value={sleepForm.startTime}
                        onChange={(e) => setSleepForm({ ...sleepForm, startTime: e.target.value })}
                      />
                    </div>
                    <div className="cd-form-group">
                      <label htmlFor="sleep-end">{t('childDev:sleeping.endTime')}</label>
                      <input
                        id="sleep-end"
                        type="time"
                        value={sleepForm.endTime}
                        onChange={(e) => setSleepForm({ ...sleepForm, endTime: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="cd-sleep-form-actions">
                    <button
                      className="cd-btn-primary"
                      onClick={() => {
                        if (!sleepForm.date || !sleepForm.startTime || !sleepForm.endTime) return;
                        const newEntry: SleepEntry = {
                          id: Date.now().toString(),
                          date: sleepForm.date,
                          startTime: sleepForm.startTime,
                          endTime: sleepForm.endTime,
                        };
                        const updated = [newEntry, ...sleepEntries];
                        saveSleep(updated, newEntry);
                        setSleepForm({ date: '', startTime: '', endTime: '' });
                        setShowSleepForm(false);
                      }}
                    >
                      Save Entry
                    </button>
                    <button
                      className="cd-btn-secondary"
                      onClick={() => {
                        setShowSleepForm(false);
                        setSleepForm({ date: '', startTime: '', endTime: '' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {sleepEntries.length === 0 ? (
                <div className="cd-empty-state">
                  <div className="cd-empty-icon-wrap">
                    <FontAwesomeIcon icon={faMoon} className="cd-empty-icon" />
                  </div>
                  <h3>{t('childDev:sleeping.noEntries')}</h3>
                  <p>{t('childDev:sleeping.noEntriesDesc')}</p>
                </div>
              ) : (
                <div className="cd-sleep-list">
                  {sleepEntries.map((entry) => (
                    <div key={entry.id} className="cd-sleep-entry-card">
                      <div className="cd-sleep-entry-info">
                        <div className="cd-sleep-entry-date">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          {new Date(entry.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="cd-sleep-entry-times">
                          <span>
                            <FontAwesomeIcon icon={faClock} /> {entry.startTime} - {entry.endTime}
                          </span>
                          <span className="cd-sleep-duration">
                            {calcSleepDuration(entry.startTime, entry.endTime)}
                          </span>
                        </div>
                      </div>
                      <button
                        className="cd-delete-btn"
                        aria-label={`Delete sleep entry for ${new Date(entry.date).toLocaleDateString('en-IN')}`}
                        onClick={() => askDelete('Delete sleep entry?', `Sleep entry for ${new Date(entry.date).toLocaleDateString('en-IN')} will be permanently removed.`, () => deleteSleepEntry(entry.id))}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════ FEEDING TAB ══════════ */}
          {activeTab === 'Feeding' && (
            <div className="cd-feeding-tab">
              <div className="cd-tab-header cd-tab-header-feeding">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faUtensils} className="cd-tab-header-icon cd-icon-feeding" />
                    {t('childDev:feeding.title')}
                  </h2>
                  <p>{t('childDev:feeding.description')}</p>
                </div>
              </div>

              {/* Date Selector */}
              <div className="cd-feeding-date-row">
                <button className="cd-cal-nav-btn" onClick={() => {
                  const d = new Date(feedingDate);
                  d.setDate(d.getDate() - 1);
                  setFeedingDate(d.toISOString().split('T')[0]);
                }} aria-label="Previous day"><FontAwesomeIcon icon={faChevronLeft} /></button>
                <input type="date" className="cd-feeding-date-input" value={feedingDate} onChange={(e) => setFeedingDate(e.target.value)} />
                <button className="cd-cal-nav-btn" onClick={() => {
                  const d = new Date(feedingDate);
                  d.setDate(d.getDate() + 1);
                  setFeedingDate(d.toISOString().split('T')[0]);
                }} aria-label="Next day"><FontAwesomeIcon icon={faChevronRight} /></button>
              </div>

              {/* Sub-tabs */}
              <div className="cd-feeding-subtabs">
                {(['breast', 'bottle', 'solid', 'diaper', 'guide', 'summary'] as const).map((st) => (
                  <button key={st} className={`cd-feeding-subtab ${feedingSubTab === st ? 'active' : ''}`} onClick={() => { setFeedingSubTab(st); setShowFeedingForm(false); setShowDiaperForm(false); }}>
                    {st === 'breast' ? t('childDev:feeding.breastfeed') : st === 'bottle' ? t('childDev:feeding.bottle') : st === 'solid' ? t('childDev:feeding.solids') : st === 'diaper' ? t('childDev:feeding.diaper') : st === 'guide' ? t('childDev:feeding.guide') : t('childDev:feeding.summary')}
                  </button>
                ))}
              </div>

              {/* Add Button */}
              {feedingSubTab !== 'summary' && feedingSubTab !== 'guide' && feedingSubTab !== 'diaper' && (
                <div className="cd-feeding-add-row">
                  <button className="cd-add-btn" onClick={() => {
                    setShowFeedingForm(!showFeedingForm);
                    setFeedingForm({ startTime: '', endTime: '', side: 'Left', amountMl: '', bottleType: 'Formula', foodDescription: '', notes: '' });
                  }}>
                    <FontAwesomeIcon icon={showFeedingForm ? faTimes : faPlus} /> {showFeedingForm ? 'Cancel' : 'Add Entry'}
                  </button>
                </div>
              )}

              {/* Feeding Form */}
              {showFeedingForm && feedingSubTab !== 'summary' && (
                <div className="cd-feeding-form-card">
                  <h3>{feedingSubTab === 'breast' ? t('childDev:feeding.logBreastfeed') : feedingSubTab === 'bottle' ? t('childDev:feeding.logBottleFeed') : t('childDev:feeding.logSolidFood')}</h3>
                  <div className="cd-feeding-form-grid">
                    <div className="cd-form-group">
                      <label>Start Time</label>
                      <input type="time" value={feedingForm.startTime} onChange={(e) => setFeedingForm({ ...feedingForm, startTime: e.target.value })} />
                    </div>
                    {feedingSubTab === 'breast' && (
                      <>
                        <div className="cd-form-group">
                          <label>End Time</label>
                          <input type="time" value={feedingForm.endTime || ''} onChange={(e) => setFeedingForm({ ...feedingForm, endTime: e.target.value })} />
                        </div>
                        <div className="cd-form-group">
                          <label>Side</label>
                          <div className="cd-feeding-side-toggle">
                            {(['Left', 'Right', 'Both'] as const).map((s) => (
                              <button key={s} className={`cd-feeding-side-btn ${feedingForm.side === s ? 'active' : ''}`} onClick={() => setFeedingForm({ ...feedingForm, side: s })}>{s}</button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {feedingSubTab === 'bottle' && (
                      <>
                        <div className="cd-form-group">
                          <label>Amount (ml)</label>
                          <input type="number" min="0" step="5" placeholder="e.g. 120" value={feedingForm.amountMl} onChange={(e) => setFeedingForm({ ...feedingForm, amountMl: e.target.value ? parseFloat(e.target.value) : '' })} />
                        </div>
                        <div className="cd-form-group">
                          <label>Type</label>
                          <div className="cd-feeding-side-toggle">
                            {(['Formula', 'Expressed Milk'] as const).map((t) => (
                              <button key={t} className={`cd-feeding-side-btn ${feedingForm.bottleType === t ? 'active' : ''}`} onClick={() => setFeedingForm({ ...feedingForm, bottleType: t })}>{t}</button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {feedingSubTab === 'solid' && (
                      <div className="cd-form-group cd-form-full-width">
                        <label>Food Description</label>
                        <input type="text" placeholder="e.g. Mashed banana, rice cereal" value={feedingForm.foodDescription || ''} onChange={(e) => setFeedingForm({ ...feedingForm, foodDescription: e.target.value })} />
                      </div>
                    )}
                    <div className="cd-form-group cd-form-full-width">
                      <label>Notes (optional)</label>
                      <input type="text" placeholder="Any additional notes" value={feedingForm.notes} onChange={(e) => setFeedingForm({ ...feedingForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="cd-feeding-form-actions">
                    <button className="cd-btn-primary" onClick={() => {
                      if (!feedingForm.startTime) return;
                      const newEntry: FeedingEntry = {
                        id: Date.now().toString(),
                        date: feedingDate,
                        type: feedingSubTab as 'breast' | 'bottle' | 'solid',
                        startTime: feedingForm.startTime,
                        endTime: feedingForm.endTime,
                        side: feedingForm.side,
                        amountMl: feedingForm.amountMl,
                        bottleType: feedingForm.bottleType,
                        foodDescription: feedingForm.foodDescription,
                        notes: feedingForm.notes,
                      };
                      saveFeedingEntries([newEntry, ...feedingEntries], newEntry);
                      setFeedingForm({ startTime: '', endTime: '', side: 'Left', amountMl: '', bottleType: 'Formula', foodDescription: '', notes: '' });
                      setShowFeedingForm(false);
                    }}>{t('childDev:growth.saveEntry')}</button>
                    <button className="cd-btn-secondary" onClick={() => setShowFeedingForm(false)}>{t('childDev:cancel')}</button>
                  </div>
                </div>
              )}

              {/* ── Diaper Sub-tab ── */}
              {feedingSubTab === 'diaper' && (
                <div className="cd-feed-diaper-section">
                  <div className="cd-feeding-add-row">
                    <button className="cd-add-btn" onClick={() => {
                      setShowDiaperForm(!showDiaperForm);
                      setDiaperForm({ time: '', type: 'wet', color: '', consistency: '', notes: '' });
                    }}>
                      <FontAwesomeIcon icon={showDiaperForm ? faTimes : faPlus} /> {showDiaperForm ? 'Cancel' : 'Add Diaper'}
                    </button>
                  </div>

                  {showDiaperForm && (
                    <div className="cd-feeding-form-card">
                      <h3>Log Diaper Change</h3>
                      <div className="cd-feeding-form-grid">
                        <div className="cd-form-group">
                          <label>Time</label>
                          <input type="time" value={diaperForm.time} onChange={(e) => setDiaperForm({ ...diaperForm, time: e.target.value })} />
                        </div>
                        <div className="cd-form-group">
                          <label>Type</label>
                          <select value={diaperForm.type} onChange={(e) => setDiaperForm({ ...diaperForm, type: e.target.value as DiaperEntry['type'] })}>
                            <option value="wet">Wet</option>
                            <option value="poop">Poop</option>
                            <option value="both">Both</option>
                            <option value="dry">Dry</option>
                          </select>
                        </div>
                        {(diaperForm.type === 'poop' || diaperForm.type === 'both') && (
                          <>
                            <div className="cd-form-group">
                              <label>Color</label>
                              <select value={diaperForm.color} onChange={(e) => setDiaperForm({ ...diaperForm, color: e.target.value })}>
                                <option value="">-- Select --</option>
                                <option value="yellow">Yellow</option>
                                <option value="green">Green</option>
                                <option value="brown">Brown</option>
                                <option value="black">Black</option>
                                <option value="red">Red (consult doctor)</option>
                                <option value="white">White (consult doctor)</option>
                              </select>
                            </div>
                            <div className="cd-form-group">
                              <label>Consistency</label>
                              <select value={diaperForm.consistency} onChange={(e) => setDiaperForm({ ...diaperForm, consistency: e.target.value })}>
                                <option value="">-- Select --</option>
                                <option value="liquid">Liquid</option>
                                <option value="soft">Soft</option>
                                <option value="formed">Formed</option>
                                <option value="hard">Hard</option>
                              </select>
                            </div>
                          </>
                        )}
                        <div className="cd-form-group cd-form-full">
                          <label>Notes (optional)</label>
                          <input type="text" value={diaperForm.notes} onChange={(e) => setDiaperForm({ ...diaperForm, notes: e.target.value })} placeholder="Any observations..." />
                        </div>
                      </div>
                      <div className="cd-feeding-form-actions">
                        <button className="cd-btn-primary" onClick={() => {
                          if (!diaperForm.time) return;
                          const newEntry: DiaperEntry = { id: Date.now().toString(), date: feedingDate, time: diaperForm.time, type: diaperForm.type, color: diaperForm.color, consistency: diaperForm.consistency, notes: diaperForm.notes };
                          saveDiaperEntries([newEntry, ...diaperEntries], newEntry);
                          setShowDiaperForm(false);
                          setDiaperForm({ time: '', type: 'wet', color: '', consistency: '', notes: '' });
                        }}>{t('childDev:save')}</button>
                        <button className="cd-btn-secondary" onClick={() => setShowDiaperForm(false)}>{t('childDev:cancel')}</button>
                      </div>
                    </div>
                  )}

                  {/* Today's diaper stats */}
                  <div className="cd-feed-diaper-stats">
                    <div className="cd-feeding-stat-card"><span className="cd-feeding-stat-value">{diaperTodayEntries.length}</span><span className="cd-feeding-stat-label">Total Changes</span></div>
                    <div className="cd-feeding-stat-card"><span className="cd-feeding-stat-value">{diaperTodayWet}</span><span className="cd-feeding-stat-label">Wet</span></div>
                    <div className="cd-feeding-stat-card"><span className="cd-feeding-stat-value">{diaperTodayPoop}</span><span className="cd-feeding-stat-label">Poop</span></div>
                  </div>

                  {/* Diaper entries list */}
                  {(() => {
                    const filtered = diaperTodayEntries.sort((a, b) => b.time.localeCompare(a.time));
                    if (filtered.length === 0) return (
                      <div className="cd-empty-state">
                        <div className="cd-empty-icon-wrap"><FontAwesomeIcon icon={faBaby} className="cd-empty-icon" /></div>
                        <h3>No diaper entries for this day</h3>
                        <p>Tap "Add Diaper" to log a change.</p>
                      </div>
                    );
                    return (
                      <div className="cd-feeding-entries-list">
                        {filtered.map((entry) => (
                          <div key={entry.id} className="cd-feeding-entry-card">
                            <div className="cd-feeding-entry-info">
                              <div className="cd-feeding-entry-time"><FontAwesomeIcon icon={faClock} /><span>{entry.time}</span></div>
                              <span className={`cd-feed-diaper-badge cd-feed-diaper-${entry.type}`}>
                                {entry.type === 'wet' ? 'Wet' : entry.type === 'poop' ? 'Poop' : entry.type === 'both' ? 'Wet + Poop' : 'Dry'}
                              </span>
                              {entry.color && <span className="cd-feeding-entry-detail">Color: {entry.color}</span>}
                              {entry.consistency && <span className="cd-feeding-entry-detail">Consistency: {entry.consistency}</span>}
                              {entry.notes && <span className="cd-feeding-entry-notes">{entry.notes}</span>}
                            </div>
                            <button className="cd-delete-btn" onClick={() => askDelete('Delete diaper entry?', 'This diaper entry will be permanently removed.', () => deleteDiaperEntry(entry.id))} aria-label="Delete diaper entry">
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── Feeding Guide Sub-tab ── */}
              {feedingSubTab === 'guide' && (
                <div className="cd-feed-guide-section">
                  <div className="cd-feed-guide-card">
                    <h3 className="cd-feed-guide-title"><FontAwesomeIcon icon={faHeart} /> Standard Feeding Requirements</h3>
                    <p className="cd-feed-guide-source">Based on AAP & WHO guidelines</p>
                    <div className="cd-feed-guide-table-wrap">
                      <table className="cd-feed-guide-table">
                        <thead>
                          <tr>
                            <th>Age</th>
                            <th>Source</th>
                            <th>Amount/Feed</th>
                            <th>Feeds/Day</th>
                            <th>Solids</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feedingGuideData.map((row) => {
                            const isCurrentAge = childAgeInMonths !== null && childAgeInMonths >= row.minMonth && childAgeInMonths < row.maxMonth;
                            return (
                              <tr key={row.ageRange} className={isCurrentAge ? 'cd-feed-guide-active-row' : ''}>
                                <td className="cd-feed-guide-age-cell">{row.ageRange}</td>
                                <td>{row.source}</td>
                                <td>{row.amountPerFeed}</td>
                                <td>{row.feedsPerDay}</td>
                                <td>{row.solids}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {childAgeInMonths !== null && <p className="cd-feed-guide-note">Your child's current age row is highlighted.</p>}
                  </div>
                </div>
              )}

              {/* Summary Sub-tab */}
              {feedingSubTab === 'summary' && (
                <div className="cd-feeding-summary">
                  <h3 className="cd-feeding-summary-title">{t('childDev:feeding.todaySummary')}</h3>
                  <div className="cd-feeding-summary-cards">
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{feedingTodayBreastCount}</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.breastfeeds')}</span>
                    </div>
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{feedingTodayBottleCount}</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.bottleFeeds')}</span>
                    </div>
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{feedingTodayBottleMl} ml</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.totalBottle')}</span>
                    </div>
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{feedingTodaySolidsCount}</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.solidMeals')}</span>
                    </div>
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{diaperTodayEntries.length}</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.diapers')}</span>
                    </div>
                    <div className="cd-feeding-stat-card">
                      <span className="cd-feeding-stat-value">{diaperTodayPoop}</span>
                      <span className="cd-feeding-stat-label">{t('childDev:feeding.poops')}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Entries list for active sub-tab (breastfeed/bottle/solids only) */}
              {(feedingSubTab === 'breast' || feedingSubTab === 'bottle' || feedingSubTab === 'solid') && (() => {
                const filtered = feedingTodayEntries.filter((e) => e.type === feedingSubTab).sort((a, b) => b.startTime.localeCompare(a.startTime));
                if (filtered.length === 0) return (
                  <div className="cd-empty-state">
                    <div className="cd-empty-icon-wrap"><FontAwesomeIcon icon={faUtensils} className="cd-empty-icon" /></div>
                    <h3>No {feedingSubTab} entries for this day</h3>
                    <p>Tap "Add Entry" to log a feeding session.</p>
                  </div>
                );
                return (
                  <div className="cd-feeding-entries-list">
                    {filtered.map((entry) => (
                      <div key={entry.id} className="cd-feeding-entry-card">
                        <div className="cd-feeding-entry-info">
                          <div className="cd-feeding-entry-time">
                            <FontAwesomeIcon icon={faClock} />
                            <span>{entry.startTime}{entry.endTime ? ` - ${entry.endTime}` : ''}</span>
                          </div>
                          {entry.type === 'breast' && entry.side && <span className="cd-feeding-entry-detail">Side: {entry.side}</span>}
                          {entry.type === 'bottle' && <span className="cd-feeding-entry-detail">{entry.amountMl ? `${entry.amountMl} ml` : ''} {entry.bottleType || ''}</span>}
                          {entry.type === 'solid' && entry.foodDescription && <span className="cd-feeding-entry-detail">{entry.foodDescription}</span>}
                          {entry.notes && <span className="cd-feeding-entry-notes">{entry.notes}</span>}
                        </div>
                        <button className="cd-delete-btn" onClick={() => askDelete('Delete feeding entry?', 'This feeding entry will be permanently removed.', () => deleteFeedingEntry(entry.id))} aria-label="Delete feeding entry">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ══════════ HEALTH TAB ══════════ */}
          {activeTab === 'Health' && (
            <div className="cd-health-tab">
              <div className="cd-tab-header cd-tab-header-health">
                <div className="cd-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={faThermometerHalf} className="cd-tab-header-icon cd-icon-health" />
                    {t('childDev:health.title')}
                  </h2>
                  <p>{t('childDev:health.description')}</p>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="cd-health-subtabs">
                <button className={`cd-health-subtab ${healthSubTab === 'temperature' ? 'active' : ''}`} onClick={() => { setHealthSubTab('temperature'); setShowHealthForm(false); }}>
                  <FontAwesomeIcon icon={faThermometerHalf} /> {t('childDev:health.temperature')}
                </button>
                <button className={`cd-health-subtab ${healthSubTab === 'medication' ? 'active' : ''}`} onClick={() => { setHealthSubTab('medication'); setShowHealthForm(false); }}>
                  <FontAwesomeIcon icon={faPills} /> {t('childDev:health.medication')}
                </button>
              </div>

              <div className="cd-health-add-row">
                <button className="cd-add-btn" onClick={() => {
                  setShowHealthForm(!showHealthForm);
                  if (healthSubTab === 'temperature') {
                    setHealthTempForm({ date: '', time: '', temperature: '', unit: 'F', notes: '' });
                  } else {
                    setHealthMedForm({ date: '', time: '', medicineName: '', dosage: '', notes: '' });
                  }
                }}>
                  <FontAwesomeIcon icon={showHealthForm ? faTimes : faPlus} /> {showHealthForm ? 'Cancel' : 'Add Entry'}
                </button>
              </div>

              {/* Temperature Form */}
              {showHealthForm && healthSubTab === 'temperature' && (
                <div className="cd-health-form-card">
                  <h3>{t('childDev:health.logTemperature')}</h3>
                  <div className="cd-health-form-grid">
                    <div className="cd-form-group">
                      <label>Date</label>
                      <input type="date" value={healthTempForm.date} onChange={(e) => setHealthTempForm({ ...healthTempForm, date: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Time</label>
                      <input type="time" value={healthTempForm.time} onChange={(e) => setHealthTempForm({ ...healthTempForm, time: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Temperature</label>
                      <input type="number" step="0.1" min="90" max="110" placeholder={healthTempForm.unit === 'F' ? 'e.g. 98.6' : 'e.g. 37.0'} value={healthTempForm.temperature} onChange={(e) => setHealthTempForm({ ...healthTempForm, temperature: e.target.value ? parseFloat(e.target.value) : '' })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Unit</label>
                      <div className="cd-feeding-side-toggle">
                        <button className={`cd-feeding-side-btn ${healthTempForm.unit === 'F' ? 'active' : ''}`} onClick={() => setHealthTempForm({ ...healthTempForm, unit: 'F' })}>F</button>
                        <button className={`cd-feeding-side-btn ${healthTempForm.unit === 'C' ? 'active' : ''}`} onClick={() => setHealthTempForm({ ...healthTempForm, unit: 'C' })}>C</button>
                      </div>
                    </div>
                    <div className="cd-form-group cd-form-full-width">
                      <label>Notes (optional)</label>
                      <input type="text" placeholder="Any observations" value={healthTempForm.notes} onChange={(e) => setHealthTempForm({ ...healthTempForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="cd-health-form-actions">
                    <button className="cd-btn-primary" onClick={() => {
                      if (!healthTempForm.date || healthTempForm.temperature === '') return;
                      const newEntry: HealthTempEntry = { id: Date.now().toString(), ...healthTempForm, temperature: Number(healthTempForm.temperature) };
                      saveHealthTempEntries([newEntry, ...healthTempEntries], newEntry);
                      setHealthTempForm({ date: '', time: '', temperature: '', unit: 'F', notes: '' });
                      setShowHealthForm(false);
                    }}>{t('childDev:save')}</button>
                    <button className="cd-btn-secondary" onClick={() => setShowHealthForm(false)}>{t('childDev:cancel')}</button>
                  </div>
                </div>
              )}

              {/* Medication Form */}
              {showHealthForm && healthSubTab === 'medication' && (
                <div className="cd-health-form-card">
                  <h3>{t('childDev:health.logMedication')}</h3>
                  <div className="cd-health-form-grid">
                    <div className="cd-form-group">
                      <label>Date</label>
                      <input type="date" value={healthMedForm.date} onChange={(e) => setHealthMedForm({ ...healthMedForm, date: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Time</label>
                      <input type="time" value={healthMedForm.time} onChange={(e) => setHealthMedForm({ ...healthMedForm, time: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Medicine Name</label>
                      <input type="text" placeholder="e.g. Crocin" value={healthMedForm.medicineName} onChange={(e) => setHealthMedForm({ ...healthMedForm, medicineName: e.target.value })} />
                    </div>
                    <div className="cd-form-group">
                      <label>Dosage</label>
                      <input type="text" placeholder="e.g. 2.5 ml" value={healthMedForm.dosage} onChange={(e) => setHealthMedForm({ ...healthMedForm, dosage: e.target.value })} />
                    </div>
                    <div className="cd-form-group cd-form-full-width">
                      <label>Notes (optional)</label>
                      <input type="text" placeholder="Any notes" value={healthMedForm.notes} onChange={(e) => setHealthMedForm({ ...healthMedForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="cd-health-form-actions">
                    <button className="cd-btn-primary" onClick={() => {
                      if (!healthMedForm.date || !healthMedForm.medicineName) return;
                      const newEntry: HealthMedEntry = { id: Date.now().toString(), ...healthMedForm };
                      saveHealthMedEntries([newEntry, ...healthMedEntries], newEntry);
                      setHealthMedForm({ date: '', time: '', medicineName: '', dosage: '', notes: '' });
                      setShowHealthForm(false);
                    }}>{t('childDev:save')}</button>
                    <button className="cd-btn-secondary" onClick={() => setShowHealthForm(false)}>{t('childDev:cancel')}</button>
                  </div>
                </div>
              )}

              {/* Temperature Entries */}
              {healthSubTab === 'temperature' && (
                healthTempEntries.length === 0 ? (
                  <div className="cd-empty-state">
                    <div className="cd-empty-icon-wrap"><FontAwesomeIcon icon={faThermometerHalf} className="cd-empty-icon" /></div>
                    <h3>{t('childDev:health.noTempReadings')}</h3>
                    <p>{t('childDev:health.noTempReadingsDesc')}</p>
                  </div>
                ) : (
                  <div className="cd-health-entries-list">
                    {[...healthTempEntries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry) => (
                      <div key={entry.id} className="cd-health-entry-card cd-health-temp-card">
                        <div className="cd-health-entry-info">
                          <div className="cd-health-entry-top">
                            <span className="cd-health-entry-value">{entry.temperature}{'\u00B0'}{entry.unit}</span>
                            <span className={`cd-health-temp-badge ${(entry.unit === 'F' && Number(entry.temperature) >= 100.4) || (entry.unit === 'C' && Number(entry.temperature) >= 38) ? 'fever' : 'normal'}`}>
                              {(entry.unit === 'F' && Number(entry.temperature) >= 100.4) || (entry.unit === 'C' && Number(entry.temperature) >= 38) ? 'Fever' : 'Normal'}
                            </span>
                          </div>
                          <div className="cd-health-entry-meta">
                            <FontAwesomeIcon icon={faCalendarAlt} /> {new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {entry.time && <><FontAwesomeIcon icon={faClock} style={{ marginLeft: 12 }} /> {entry.time}</>}
                          </div>
                          {entry.notes && <span className="cd-health-entry-notes">{entry.notes}</span>}
                        </div>
                        <button className="cd-delete-btn" onClick={() => askDelete('Delete temperature entry?', 'This temperature entry will be permanently removed.', () => deleteHealthTempEntry(entry.id))} aria-label="Delete temperature entry">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Medication Entries */}
              {healthSubTab === 'medication' && (
                healthMedEntries.length === 0 ? (
                  <div className="cd-empty-state">
                    <div className="cd-empty-icon-wrap"><FontAwesomeIcon icon={faPills} className="cd-empty-icon" /></div>
                    <h3>{t('childDev:health.noMedEntries')}</h3>
                    <p>{t('childDev:health.noMedEntriesDesc')}</p>
                  </div>
                ) : (
                  <div className="cd-health-entries-list">
                    {[...healthMedEntries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry) => (
                      <div key={entry.id} className="cd-health-entry-card cd-health-med-card">
                        <div className="cd-health-entry-info">
                          <div className="cd-health-entry-top">
                            <span className="cd-health-entry-value">{entry.medicineName}</span>
                            {entry.dosage && <span className="cd-health-med-dosage">{entry.dosage}</span>}
                          </div>
                          <div className="cd-health-entry-meta">
                            <FontAwesomeIcon icon={faCalendarAlt} /> {new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {entry.time && <><FontAwesomeIcon icon={faClock} style={{ marginLeft: 12 }} /> {entry.time}</>}
                          </div>
                          {entry.notes && <span className="cd-health-entry-notes">{entry.notes}</span>}
                        </div>
                        <button className="cd-delete-btn" onClick={() => askDelete('Delete medication entry?', 'This medication entry will be permanently removed.', () => deleteHealthMedEntry(entry.id))} aria-label="Delete medication entry">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </Container>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={deleteConfirm?.title || ''}
        message={deleteConfirm?.message || ''}
        onConfirm={() => deleteConfirm?.onConfirm()}
        onClose={() => setDeleteConfirm(null)}
        confirmLabel="Delete"
        variant="danger"
      />

    </div>
  );
};

export default ChildDevelopment;
